import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  direction: 'ltr' | 'rtl';
}

interface Translation {
  [key: string]: {
    [languageCode: string]: string;
  };
}

interface LanguageContextType {
  currentLanguage: string;
  setLanguage: (languageCode: string) => void;
  t: (key: string) => string;
  languages: Language[];
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

// Translation data
const translations: Translation = {
  // Navigation
  'nav.home': {
    en: 'Home',
    hi: 'होम',
    pa: 'ਹੋਮ'
  },
  'nav.get_measured': {
    en: 'Get Measured',
    hi: 'माप लें',
    pa: 'ਮਾਪ ਲਵੋ'
  },
  'nav.my_results': {
    en: 'My Results',
    hi: 'मेरे परिणाम',
    pa: 'ਮੇਰੇ ਨਤੀਜੇ'
  },
  'nav.try_on': {
    en: 'Try On',
    hi: 'पहन कर देखें',
    pa: 'ਪਹਿਨ ਕੇ ਦੇਖੋ'
  },
  'nav.shop': {
    en: 'Shop',
    hi: 'दुकान',
    pa: 'ਦੁਕਾਨ'
  },
  'nav.tailor_dashboard': {
    en: 'MasterJi Dashboard',
    hi: 'मास्टरजी डैशबोर्ड',
    pa: 'ਮਾਸਟਰਜੀ ਡੈਸ਼ਬੋਰਡ'
  },
  'nav.user_dashboard': {
    en: 'User Dashboard',
    hi: 'उपयोगकर्ता डैशबोर्ड',
    pa: 'ਯੂਜ਼ਰ ਡੈਸ਼ਬੋਰਡ'
  },
  'nav.welcome': {
    en: 'Welcome to MasterJi',
    hi: 'मास्टरजी में आपका स्वागत है',
    pa: 'ਮਾਸਟਰਜੀ ਵਿੱਚ ਤੁਹਾਡਾ ਸਵਾਗਤ ਹੈ'
  },
  'nav.track_order': {
    en: 'Track Order',
    hi: 'ऑर्डर ट्रैक करें',
    pa: 'ਆਰਡਰ ਟ੍ਰੈਕ ਕਰੋ'
  },
  'nav.features': {
    en: 'Features',
    hi: 'विशेषताएं',
    pa: 'ਵਿਸ਼ੇਸ਼ਤਾਵਾਂ',
    bn: 'বৈশিষ্ট্য',
    te: 'లక్షణాలు',
    ta: 'அம்சங்கள்',
    ml: 'സവിശേഷതകൾ',
    kn: 'ವೈಶಿಷ್ಟ್ಯಗಳು',
    gu: 'વિશેષતાઓ',
    mr: 'वैशिष्ट्ये'
  },
  'nav.pricing': {
    en: 'Pricing',
    hi: 'मूल्य निर्धारण',
    pa: 'ਕੀਮਤ',
    bn: 'মূল্য',
    te: 'ధర',
    ta: 'விலை',
    ml: 'വില',
    kn: 'ಬೆಲೆ',
    gu: 'કિંમત',
    mr: 'किंमत'
  },
  'nav.help_center': {
    en: 'Help',
    hi: 'सहायता',
    pa: 'ਮਦਦ',
    bn: 'সাহায্য',
    te: 'సహాయం',
    ta: 'உதவி',
    ml: 'സഹായം',
    kn: 'ಸಹಾಯ',
    gu: 'મદદ',
    mr: 'मदत'
  },
  'nav.contact': {
    en: 'Contact',
    hi: 'संपर्क',
    pa: 'ਸੰਪਰਕ',
    bn: 'যোগাযোগ',
    te: 'సంప్రదింపు',
    ta: 'தொடர்பு',
    ml: 'ബന്ധപ്പെടുക',
    kn: 'ಸಂಪರ್ಕ',
    gu: 'સંપર્ક',
    mr: 'संपर्क'
  },
  'nav.about': {
    en: 'About',
    hi: 'के बारे में',
    pa: 'ਬਾਰੇ',
    bn: 'সম্পর্কে',
    te: 'గురించి',
    ta: 'பற்றி',
    ml: 'കുറിച്ച്',
    kn: 'ಕುರಿತು',
    gu: 'વિશે',
    mr: 'बद्दल'
  },
  'nav.privacy_policy': {
    en: 'Privacy',
    hi: 'गोपनीयता',
    pa: 'ਗੋਪਨੀਯਤਾ',
    bn: 'গোপনীয়তা',
    te: 'గోప్యత',
    ta: 'தனியுரிமை',
    ml: 'സ്വകാര്യത',
    kn: 'ಗೌಪ್ಯತೆ',
    gu: 'ગોપનીયતા',
    mr: 'गोपनीयता'
  },
  'nav.terms_of_service': {
    en: 'Terms',
    hi: 'शर्तें',
    pa: 'ਸ਼ਰਤਾਂ',
    bn: 'শর্তাবলী',
    te: 'నిబంధనలు',
    ta: 'விதிமுறைகள்',
    ml: 'നിബന്ധനകൾ',
    kn: 'ನಿಯಮಗಳು',
    gu: 'નિયમો',
    mr: 'अटी'
  },
  'nav.how_it_works': {
    en: 'How It Works',
    hi: 'यह कैसे काम करता है',
    pa: 'ਇਹ ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ',
    bn: 'এটি কীভাবে কাজ করে',
    te: 'ఇది ఎలా పని చేస్తుంది',
    ta: 'இது எப்படி வேலை செய்கிறது',
    ml: 'ഇത് എങ്ങനെ പ്രവർത്തിക്കുന്നു',
    kn: 'ಇದು ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ',
    gu: 'આ કેવી રીતે કામ કરે છે',
    mr: 'हे कसे काम करते'
  },
  'nav.about_us': {
    en: 'About us',
    hi: 'हमारे बारे में',
    pa: 'ਸਾਡੇ ਬਾਰੇ',
    bn: 'আমাদের সম্পর্কে',
    te: 'మా గురించి',
    ta: 'எங்களைப் பற்றி',
    ml: 'ഞങ്ങളെക്കുറിച്ച്',
    kn: 'ನಮ್ಮ ಬಗ್ಗೆ',
    gu: 'અમારા વિશે',
    mr: 'आमच्याबद्दल'
  },
  'nav.faqs': {
    en: 'FAQs',
    hi: 'अक्सर पूछे जाने वाले प्रश्न',
    pa: 'ਅਕਸਰ ਪੁੱਛੇ ਜਾਣ ਵਾਲੇ ਪ੍ਰਸ਼ਨ',
    bn: 'প্রায়শই জিজ্ঞাসিত প্রশ্ন',
    te: 'తరచుగా అడిగే ప్రశ్నలు',
    ta: 'அடிக்கடி கேட்கப்படும் கேள்விகள்',
    ml: 'പലപ്പോഴും ചോദിക്കുന്ന ചോദ്യങ്ങൾ',
    kn: 'ಆಗಾಗ್ಗೆ ಕೇಳಲಾಗುವ ಪ್ರಶ್ನೆಗಳು',
    gu: 'ઘણી વાર પૂછાતા પ્રશ્નો',
    mr: 'वारंवार विचारले जाणारे प्रश्न'
  },
  'nav.contact_us': {
    en: 'Contact us',
    hi: 'हमसे संपर्क करें',
    pa: 'ਸਾਡੇ ਨਾਲ ਸੰਪਰਕ ਕਰੋ',
    bn: 'আমাদের সাথে যোগাযোগ করুন',
    te: 'మమ్మల్ని సంప్రదించండి',
    ta: 'எங்களைத் தொடர்பு குறியுங்கள்',
    ml: 'ഞങ്ങളെ ബന്ധപ്പെടുക',
    kn: 'ನಮ್ಮನ್ನು ಸಂಪರ್ಕಿಸಿ',
    gu: 'અમારો સંપર્ક કરો',
    mr: 'आमच्याशी संपर्क साधा'
  },
  'nav.services': {
    en: 'Services',
    hi: 'सेवाएं',
    pa: 'ਸੇਵਾਵਾਂ',
    bn: 'সেবাসমূহ',
    te: 'సేవలు',
    ta: 'சேவைகள்',
    ml: 'സേവനങ്ങൾ',
    kn: 'ಸೇವೆಗಳು',
    gu: 'સેવાઓ',
    mr: 'सेवा'
  },
  'nav.login': {
    en: 'Log in',
    hi: 'लॉग इन करें',
    pa: 'ਲਾਗ ਇਨ ਕਰੋ',
    bn: 'লগইন করুন',
    te: 'లాగిన్ చేయండి',
    ta: 'உள்நுழைய',
    ml: 'ലോഗിൻ ചെയ്യുക',
    kn: 'ಲಾಗ್ ಇನ್ ಮಾಡಿ',
    gu: 'લૉગ ઇન કરો',
    mr: 'लॉग इन करा'
  },
  'nav.get_started': {
    en: 'Get Started',
    hi: 'शुरू करें',
    pa: 'ਸ਼ੁਰੂ ਕਰੋ',
    bn: 'শুরু করুন',
    te: 'ప్రారంభించండి',
    ta: 'தொடங்கவும்',
    ml: 'ആരംഭിക്കുക',
    kn: 'ಪ್ರಾರಂಭಿಸಿ',
    gu: 'શરૂ કરો',
    mr: 'सुरू करा'
  },
  'nav.sign_out': {
    en: 'Sign Out',
    hi: 'साइन आउट करें',
    pa: 'ਸਾਈਨ ਆਉਟ ਕਰੋ',
    bn: 'সাইন আউট করুন',
    te: 'సైన్ అవుట్ చేయండి',
    ta: 'வெளியேறு',
    ml: 'സൈൻ ഔട്ട് ചെയ്യുക',
    kn: 'ಸೈನ್ ಔಟ್ ಮಾಡಿ',
    gu: 'સાઇન આઉટ કરો',
    mr: 'साइन आउट करा'
  },
  'nav.admin_dashboard': {
    en: 'Admin Dashboard',
    hi: 'एडमिन डैशबोर्ड',
    pa: 'ਐਡਮਿਨ ਡੈਸ਼ਬੋਰਡ',
    bn: 'অ্যাডমিন ড্যাশবোর্ড',
    te: 'అడ్మిన్ డాష్‌బోర్డ్',
    ta: 'நிர்வாக கட்டுப்பாட்டு பலகை',
    ml: 'അഡ്‌മിൻ ഡാഷ്‌ബോർഡ്',
    kn: 'ನಿರ್ವಾಹಕ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್',
    gu: 'એડમિન ડેશબોર્ડ',
    mr: 'प्रशासक डॅशबोर्ड'
  },
  'nav.business_dashboard': {
    en: 'Business Dashboard',
    hi: 'व्यवसाय डैशबोर्ड',
    pa: 'ਵਪਾਰ ਡੈਸ਼ਬੋਰਡ',
    bn: 'ব্যবসা ড্যাশবোর্ড',
    te: 'వ్యాపార డాష్‌బోర్డ్',
    ta: 'வணிக கட்டுப்பாட்டு பலகை',
    ml: 'ബിസിനസ് ഡാഷ്‌ബോർഡ്',
    kn: 'ವ್ಯವಹಾರ ಡ್ಯಾಶ್‌ಬೋರ್ಡ್',
    gu: 'વ્યવસાય ડેશબોર્ડ',
    mr: 'व्यवसाय डॅशबोर्ड'
  },
  'nav.my_account': {
    en: 'My Account',
    hi: 'मेरा खाता',
    pa: 'ਮੇਰਾ ਖਾਤਾ',
    bn: 'আমার অ্যাকাউন্ট',
    te: 'నా ఖాతా',
    ta: 'என் கணக்கு',
    ml: 'എന്റെ അക്കൗണ്ട്',
    kn: 'ನನ್ನ ಖಾತೆ',
    gu: 'મારું એકાઉન્ટ',
    mr: 'माझे खाते'
  },
  'footer.quick_links': {
    en: 'Quick Links',
    hi: 'त्वरित लिंक',
    pa: 'ਤੇਜ਼ ਲਿੰਕ',
    bn: 'দ্রুত লিঙ্ক',
    te: 'వేగవంతమైన లింక్‌లు',
    ta: 'விரைவு இணைப்புகள்',
    ml: 'വേഗ ലിങ്കുകൾ',
    kn: 'ತ್ವರಿತ ಲಿಂಕ್‌ಗಳು',
    gu: 'ઝડપી લિંક્સ',
    mr: 'त्वरित दुवे'
  },
  'footer.home': {
    en: 'Home',
    hi: 'होम',
    pa: 'ਹੋਮ',
    bn: 'হোম',
    te: 'హోమ్',
    ta: 'முகப்பு',
    ml: 'ഹോം',
    kn: 'ಮುಖಪುಟ',
    gu: 'હોમ',
    mr: 'मुख्यपृष्ठ'
  },
  'footer.follow_us': {
    en: 'Follow us:',
    hi: 'हमें फॉलो करें:',
    pa: 'ਸਾਨੂੰ ਫਾਲੋ ਕਰੋ:',
    bn: 'আমাদের অনুসরণ করুন:',
    te: 'మమ్మల్ని అనుసరించండి:',
    ta: 'எங்களைப் பின்தொடரவும்:',
    ml: 'ഞങ്ങളെ പിന്തുടരുക:',
    kn: 'ನಮ್ಮನ್ನು ಅನುಸರಿಸಿ:',
    gu: 'અમને ફોલો કરો:',
    mr: 'आम्हाला फॉलो करा:'
  },
  'footer.join_community': {
    en: 'Join our community for updates and insights.',
    hi: 'अपडेट और अंतर्दृष्टि के लिए हमारे समुदाय में शामिल हों।',
    pa: 'ਅਪਡੇਟ ਅਤੇ ਸੂਝਾਂ ਲਈ ਸਾਡੇ ਸਮੂਹ ਵਿੱਚ ਸ਼ਾਮਲ ਹੋਵੋ।',
    bn: 'আপডেট এবং অন্তর্দৃষ্টির জন্য আমাদের সম্প্রদায়ে যোগ দিন।',
    te: 'నవీకరణలు మరియు అంతర్దృష్టుల కోసం మా సమాజంలో చేరండి.',
    ta: 'புதுப்பிப்புகள் மற்றும் நுண்ணறிவுக்காக எங்கள் சமூகத்தில் சேரவும்.',
    ml: 'അപ്ഡേറ്റുകളും ഉൾക്കാഴ്ചകളും ലഭിക്കാൻ ഞങ്ങളുടെ സമൂഹത്തിൽ ചേരുക.',
    kn: 'ನವೀಕರಣಗಳು ಮತ್ತು ಒಳನೋಟಗಳಿಗಾಗಿ ನಮ್ಮ ಸಮುದಾಯದಲ್ಲಿ ಸೇರಿಕೊಳ್ಳಿ.',
    gu: 'અપડેટ્સ અને સમજણ માટે અમારા સમુદાયમાં જોડાઓ.',
    mr: 'अपडेट्स आणि अंतर्दृष्टीसाठी आमच्या समुदायात सामील व्हा.'
  },

  // Dashboard
  'dashboard.welcome': {
    en: 'Welcome to Virtual Tailor',
    hi: 'वर्चुअल टेलर में आपका स्वागत है',
    pa: 'ਵਰਚੁਅਲ ਟੇਲਰ ਵਿੱਚ ਤੁਹਾਡਾ ਸਵਾਗਤ ਹੈ'
  },
  'dashboard.quick_actions': {
    en: 'Quick Actions',
    hi: 'त्वरित कार्य',
    pa: 'ਤੇਜ਼ ਕਾਰਵਾਈ'
  },
  'dashboard.new_order': {
    en: 'New Order',
    hi: 'नया ऑर्डर',
    pa: 'ਨਵਾਂ ਆਰਡਰ'
  },
  'dashboard.customer_directory': {
    en: 'Customer Directory',
    hi: 'ग्राहक निर्देशिका',
    pa: 'ਗਾਹਕ ਡਾਇਰੈਕਟਰੀ'
  },
  'dashboard.order_management': {
    en: 'Order Management',
    hi: 'ऑर्डर प्रबंधन',
    pa: 'ਆਰਡਰ ਪ੍ਰਬੰਧਨ'
  },
  'dashboard.search_records': {
    en: 'Search Records',
    hi: 'रिकॉर्ड खोजें',
    pa: 'ਰਿਕਾਰਡ ਖੋਜੋ'
  },
  'dashboard.print_export': {
    en: 'Print & Export',
    hi: 'प्रिंट और निर्यात',
    pa: 'ਪ੍ਰਿੰਟ ਅਤੇ ਨਿਰਯਾਤ'
  },
  'dashboard.measurement_book': {
    en: 'Measurement Book',
    hi: 'माप पुस्तक',
    pa: 'ਮਾਪ ਪੁਸਤਕ'
  },
  'dashboard.analytics': {
    en: 'Analytics',
    hi: 'विश्लेषण',
    pa: 'ਵਿਸ਼ਲੇਸ਼ਣ'
  },
  'dashboard.settings': {
    en: 'Settings',
    hi: 'सेटिंग्स',
    pa: 'ਸੈਟਿੰਗਾਂ'
  },

  // Settings
  'settings.language_settings': {
    en: 'Language Settings',
    hi: 'भाषा सेटिंग्स',
    pa: 'ਭਾਸ਼ਾ ਸੈਟਿੰਗਾਂ'
  },
  'settings.choose_language': {
    en: 'Choose your preferred language',
    hi: 'अपनी पसंदीदा भाषा चुनें',
    pa: 'ਆਪਣੀ ਪਸੰਦੀਦਾ ਭਾਸ਼ਾ ਚੁਣੋ'
  },
  'settings.google_translate': {
    en: 'Google Translate',
    hi: 'गूगल अनुवाद',
    pa: 'ਗੂਗਲ ਅਨੁਵਾਦ'
  },
  'settings.auto_translate': {
    en: 'Enable automatic translation for additional languages',
    hi: 'अतिरिक्त भाषाओं के लिए स्वचालित अनुवाद सक्षम करें',
    pa: 'ਵਾਧੂ ਭਾਸ਼ਾਵਾਂ ਲਈ ਆਟੋਮੈਟਿਕ ਅਨੁਵਾਦ ਸਮਰੱਥ ਕਰੋ'
  },
  'settings.shop_information': {
    en: 'Shop Information',
    hi: 'दुकान की जानकारी',
    pa: 'ਦੁਕਾਨ ਦੀ ਜਾਣਕਾਰੀ'
  },
  'settings.customize_shop': {
    en: 'Customize your shop details',
    hi: 'अपने दुकान के विवरण को अनुकूलित करें',
    pa: 'ਆਪਣੇ ਦੁਕਾਨ ਦੇ ਵੇਰਵੇ ਨੂੰ ਅਨੁਕੂਲ ਬਣਾਓ'
  },
  'settings.backup_restore': {
    en: 'Backup & Restore',
    hi: 'बैकअप और पुनर्स्थापना',
    pa: 'ਬੈਕਅੱਪ ਅਤੇ ਪੁਨਰਸਥਾਪਨ'
  },
  'settings.save_restore': {
    en: 'Save and restore your settings',
    hi: 'अपनी सेटिंग्स को सहेजें और पुनर्स्थापित करें',
    pa: 'ਆਪਣੀਆਂ ਸੈਟਿੰਗਾਂ ਨੂੰ ਸੇਵ ਅਤੇ ਪੁਨਰਸਥਾਪਿਤ ਕਰੋ'
  },

  // Common
  'common.active': {
    en: 'Active',
    hi: 'सक्रिय',
    pa: 'ਸਰਗਰਮ'
  },
  'common.save': {
    en: 'Save',
    hi: 'सहेजें',
    pa: 'ਸੇਵ ਕਰੋ'
  },
  'common.cancel': {
    en: 'Cancel',
    hi: 'रद्द करें',
    pa: 'ਰੱਦ ਕਰੋ'
  },
  'common.loading': {
    en: 'Loading...',
    hi: 'लोड हो रहा है...',
    pa: 'ਲੋਡ ਹੋ ਰਿਹਾ ਹੈ...'
  },
  'common.error': {
    en: 'Error',
    hi: 'त्रुटि',
    pa: 'ਗਲਤੀ'
  },
  'common.success': {
    en: 'Success',
    hi: 'सफलता',
    pa: 'ਸਫਲਤਾ'
  },

  // Digital Marketplace Landing Page
  'landing.hero.title': {
    en: 'Master Ji',
    hi: 'मास्टर जी',
    pa: 'ਮਾਸਟਰ ਜੀ',
    bn: 'মাস্টার জি',
    te: 'మాస్టర్ జీ',
    ta: 'மாஸ்டர் ஜி',
    ml: 'മാസ്റ്റർ ജി',
    kn: 'ಮಾಸ್ಟರ್ ಜಿ',
    gu: 'માસ્ટર જી',
    mr: 'मास्टर जी'
  },
  'landing.hero.tagline': {
    en: 'Stitch. Manage. Grow.',
    hi: 'सिलाई। प्रबंधन। विकास।',
    pa: 'ਸਿਲਾਈ। ਪ੍ਰਬੰਧਨ। ਵਿਕਾਸ।',
    bn: 'সেলাই। পরিচালনা। বৃদ্ধি।',
    te: 'కుట్టు। నిర్వహణ। పెంపు।',
    ta: 'தைப்பு। நிர்வாகம்। வளர்ச்சி।',
    ml: 'തുന്നൽ। നിയന്ത്രണം। വളർച്ച।',
    kn: 'ಹೊಲಿಗೆ। ನಿರ್ವಹಣೆ। ಬೆಳವಣಿಗೆ।',
    gu: 'સીવણ। વ્યવસ્થાપન। વૃદ્ધિ।',
    mr: 'शिवण। व्यवस्थापन। वाढ।'
  },
  'landing.hero.story.p1': {
    en: 'At Master Ji, we believe clothing is more than just fabric — it\'s expression, heritage, and care stitched into every detail.',
    hi: 'मास्टर जी में, हम मानते हैं कि कपड़े सिर्फ कपड़ा नहीं हैं — यह हर विवरण में सिली हुई अभिव्यक्ति, विरासत और देखभाल है।',
    pa: 'ਮਾਸਟਰ ਜੀ ਵਿੱਚ, ਅਸੀਂ ਮੰਨਦੇ ਹਾਂ ਕਿ ਕੱਪੜੇ ਸਿਰਫ ਕੱਪੜਾ ਨਹੀਂ ਹਨ — ਇਹ ਹਰ ਵੇਰਵੇ ਵਿੱਚ ਸਿਲੀ ਹੋਈ ਸਮਝ, ਵਿਰਾਸਤ ਅਤੇ ਦੇਖਭਾਲ ਹੈ।'
  },
  'landing.hero.story.p2': {
    en: 'For customers, Master Ji is a welcoming space to track orders from their chosen vendors, review their saved measurements with ease, and enjoy a seamless experience — always free of charge.',
    hi: 'ग्राहकों के लिए, मास्टर जी एक स्वागत योग्य स्थान है जहां वे अपने चुने हुए विक्रेताओं से ऑर्डर ट्रैक कर सकते हैं, अपने सहेजे गए मापों को आसानी से देख सकते हैं, और एक निर्बाध अनुभव का आनंद ले सकते हैं — हमेशा मुफ्त में।',
    pa: 'ਗਾਹਕਾਂ ਲਈ, ਮਾਸਟਰ ਜੀ ਇੱਕ ਸਵਾਗਤ ਯੋਗ ਸਥਾਨ ਹੈ ਜਿੱਥੇ ਉਹ ਆਪਣੇ ਚੁਣੇ ਹੋਏ ਵਿਕਰੇਤਾਵਾਂ ਤੋਂ ਆਰਡਰ ਟ੍ਰੈਕ ਕਰ ਸਕਦੇ ਹਨ, ਆਪਣੇ ਸੁਰੱਖਿਅਤ ਮਾਪਾਂ ਨੂੰ ਆਸਾਨੀ ਨਾਲ ਦੇਖ ਸਕਦੇ ਹਨ, ਅਤੇ ਇੱਕ ਨਿਰਬਾਧ ਅਨੁਭਵ ਦਾ ਆਨੰਦ ਲੈ ਸਕਦੇ ਹਨ — ਹਮੇਸ਼ਾ ਮੁਫਤ।'
  },
  'landing.hero.story.p3': {
    en: 'For businesses, Master Ji provides simple yet powerful tools to manage orders, showcase craftsmanship, and grow alongside a supportive community.',
    hi: 'व्यवसायों के लिए, मास्टर जी ऑर्डर प्रबंधित करने, कारीगरी प्रदर्शित करने और एक सहायक समुदाय के साथ बढ़ने के लिए सरल लेकिन शक्तिशाली उपकरण प्रदान करता है।',
    pa: 'ਵਪਾਰਾਂ ਲਈ, ਮਾਸਟਰ ਜੀ ਆਰਡਰ ਪ੍ਰਬੰਧਿਤ ਕਰਨ, ਕਾਰੀਗਰੀ ਦਿਖਾਉਣ ਅਤੇ ਇੱਕ ਸਹਾਇਕ ਸਮੁਦਾਯ ਦੇ ਨਾਲ ਵਧਣ ਲਈ ਸਰਲ ਪਰ ਸ਼ਕਤੀਸ਼ਾਲੀ ਟੂਲ ਪ੍ਰਦਾਨ ਕਰਦਾ ਹੈ।'
  },
  'landing.hero.story.p4': {
    en: 'We\'re here to keep traditions alive while embracing modern convenience — a space where customers and creators thrive side by side, and where every stitch tells a story.',
    hi: 'हम यहां परंपराओं को जीवित रखने के लिए हैं जबकि आधुनिक सुविधा को अपनाते हुए — एक ऐसा स्थान जहां ग्राहक और रचनाकार साथ-साथ फलते-फूलते हैं, और जहां हर सिलाई एक कहानी कहती है।',
    pa: 'ਅਸੀਂ ਇੱਥੇ ਹਾਂ ਪਰੰਪਰਾਵਾਂ ਨੂੰ ਜੀਵੰਤ ਰੱਖਣ ਲਈ ਜਦੋਂ ਕਿ ਆਧੁਨਿਕ ਸੁਵਿਧਾ ਨੂੰ ਅਪਣਾਉਂਦੇ ਹੋਏ — ਇੱਕ ਅਜਿਹਾ ਸਥਾਨ ਜਿੱਥੇ ਗਾਹਕ ਅਤੇ ਰਚਨਾਕਾਰ ਇਕੱਠੇ ਫਲਦੇ-ਫੁੱਲਦੇ ਹਨ, ਅਤੇ ਜਿੱਥੇ ਹਰ ਸਿਲਾਈ ਇੱਕ ਕਹਾਣੀ ਦੱਸਦੀ ਹੈ।'
  },
  'landing.choice.title': {
    en: 'Choose Your Path',
    hi: 'अपना रास्ता चुनें',
    pa: 'ਆਪਣਾ ਰਾਹ ਚੁਣੋ'
  },
  'landing.choice.subtitle': {
    en: 'Customers order for free, businesses scale with powerful tools',
    hi: 'ग्राहक मुफ्त में ऑर्डर करते हैं, व्यवसाय शक्तिशाली उपकरणों के साथ बढ़ते हैं',
    pa: 'ਗਾਹਕ ਮੁਫਤ ਵਿੱਚ ਆਰਡਰ ਕਰਦੇ ਹਨ, ਵਪਾਰ ਸ਼ਕਤੀਸ਼ਾਲੀ ਟੂਲਾਂ ਨਾਲ ਵਧਦੇ ਹਨ'
  },
  'landing.customer.title': {
    en: 'Customers',
    hi: 'ग्राहक',
    pa: 'ਗਾਹਕ'
  },
  'landing.customer.subtitle': {
    en: 'Free Forever',
    hi: 'हमेशा मुफ्त',
    pa: 'ਹਮੇਸ਼ਾ ਮੁਫਤ'
  },
  'landing.customer.description': {
    en: 'Master Ji makes it simple to stay connected with your own tailor throughout the entire process. No searching, no hassle — just one place to track and manage everything.',
    hi: 'मास्टर जी पूरी प्रक्रिया के दौरान अपने दर्जी के साथ जुड़े रहना आसान बनाता है। कोई खोज नहीं, कोई परेशानी नहीं — सिर्फ सब कुछ ट्रैक और प्रबंधित करने के लिए एक जगह।',
    pa: 'ਮਾਸਟਰ ਜੀ ਪੂਰੀ ਪ੍ਰਕਿਰਿਆ ਦੇ ਦੌਰਾਨ ਆਪਣੇ ਦਰਜ਼ੀ ਨਾਲ ਜੁੜੇ ਰਹਿਣਾ ਸੌਖਾ ਬਣਾਉਂਦਾ ਹੈ। ਕੋਈ ਖੋਜ ਨਹੀਂ, ਕੋਈ ਤੰਗੀ ਨਹੀਂ — ਸਿਰਫ ਸਭ ਕੁਝ ਟ੍ਰੈਕ ਅਤੇ ਪ੍ਰਬੰਧਿਤ ਕਰਨ ਲਈ ਇੱਕ ਜਗ੍ਹਾ।'
  },
  'landing.customer.button': {
    en: 'Start Ordering',
    hi: 'ऑर्डर शुरू करें',
    pa: 'ਆਰਡਰ ਸ਼ੁਰੂ ਕਰੋ'
  },
  'landing.business.title': {
    en: 'Businesses',
    hi: 'व्यवसाय',
    pa: 'ਵਪਾਰ'
  },
  'landing.business.subtitle': {
    en: 'Grow Your Business',
    hi: 'अपना व्यवसाय बढ़ाएं',
    pa: 'ਆਪਣਾ ਵਪਾਰ ਵਧਾਓ'
  },
  'landing.business.description': {
    en: 'Whether you are a Tailor or you are Designer, Master Ji gives you the tools to focus on what you do best — creating exceptional clothing — while we take care of the management side. From handling orders to nurturing customer relationships, everything is designed to help your business grow with ease.',
    hi: 'चाहे आप दर्जी हों या डिजाइनर, मास्टर जी आपको वह उपकरण देता है जो आप सबसे अच्छा करते हैं — असाधारण कपड़े बनाना — जबकि हम प्रबंधन पक्ष का ध्यान रखते हैं। ऑर्डर संभालने से लेकर ग्राहक संबंधों को पोषित करने तक, सब कुछ आपके व्यवसाय को आसानी से बढ़ने में मदद करने के लिए डिज़ाइन किया गया है।',
    pa: 'ਚਾਹੇ ਤੁਸੀਂ ਦਰਜ਼ੀ ਹੋ ਜਾਂ ਡਿਜ਼ਾਈਨਰ, ਮਾਸਟਰ ਜੀ ਤੁਹਾਨੂੰ ਉਹ ਟੂਲ ਦਿੰਦਾ ਹੈ ਜੋ ਤੁਸੀਂ ਸਭ ਤੋਂ ਵਧੀਆ ਕਰਦੇ ਹੋ — ਸ਼ਾਨਦਾਰ ਕੱਪੜੇ ਬਣਾਉਣਾ — ਜਦੋਂ ਕਿ ਅਸੀਂ ਪ੍ਰਬੰਧਨ ਪੱਖ ਦਾ ਧਿਆਨ ਰੱਖਦੇ ਹਾਂ। ਆਰਡਰ ਸੰਭਾਲਣ ਤੋਂ ਲੈ ਕੇ ਗਾਹਕ ਸੰਬੰਧਾਂ ਨੂੰ ਪਾਲਣ ਤੱਕ, ਸਭ ਕੁਝ ਤੁਹਾਡੇ ਵਪਾਰ ਨੂੰ ਆਸਾਨੀ ਨਾਲ ਵਧਣ ਵਿੱਚ ਮਦਦ ਕਰਨ ਲਈ ਤਿਆਰ ਕੀਤਾ ਗਿਆ ਹੈ।'
  },
  'landing.business.button': {
    en: 'Start Free Trial',
    hi: 'मुफ्त ट्रायल शुरू करें',
    pa: 'ਮੁਫਤ ਟਰਾਇਲ ਸ਼ੁਰੂ ਕਰੋ'
  },
  'landing.cta.title': {
    en: 'Ready to Begin Your Journey?',
    hi: 'अपनी यात्रा शुरू करने के लिए तैयार हैं?',
    pa: 'ਆਪਣੀ ਯਾਤਰਾ ਸ਼ੁਰੂ ਕਰਨ ਲਈ ਤਿਆਰ ਹੋ?'
  },
  'landing.cta.subtitle': {
    en: 'Join the community of customers and creators who trust Master Ji for quality, craftsmanship, and growth.',
    hi: 'ग्राहकों और रचनाकारों के समुदाय में शामिल हों जो गुणवत्ता, कारीगरी और विकास के लिए मास्टर जी पर भरोसा करते हैं।',
    pa: 'ਗਾਹਕਾਂ ਅਤੇ ਰਚਨਾਕਾਰਾਂ ਦੇ ਸਮੂਹ ਵਿੱਚ ਸ਼ਾਮਲ ਹੋਵੋ ਜੋ ਗੁਣਵੱਤਾ, ਕਾਰੀਗਰੀ ਅਤੇ ਵਿਕਾਸ ਲਈ ਮਾਸਟਰ ਜੀ ਤੇ ਭਰੋਸਾ ਕਰਦੇ ਹਨ।'
  },
  'landing.customer.feature1': {
    en: 'Track orders in real time and see the progress of your garments',
    hi: 'ऑर्डर को रीयल टाइम में ट्रैक करें और अपने कपड़ों की प्रगति देखें',
    pa: 'ਆਰਡਰ ਨੂੰ ਰੀਅਲ ਟਾਈਮ ਵਿੱਚ ਟ੍ਰੈਕ ਕਰੋ ਅਤੇ ਆਪਣੇ ਕੱਪੜਿਆਂ ਦੀ ਤਰੱਕੀ ਦੇਖੋ'
  },
  'landing.customer.feature2': {
    en: 'Review and manage your measurements so fittings are always accurate',
    hi: 'अपने मापों की समीक्षा और प्रबंधन करें ताकि फिटिंग हमेशा सटीक रहे',
    pa: 'ਆਪਣੇ ਮਾਪਾਂ ਦੀ ਸਮੀਖਿਆ ਅਤੇ ਪ੍ਰਬੰਧਨ ਕਰੋ ਤਾਂ ਜੋ ਫਿਟਿੰਗ ਹਮੇਸ਼ਾ ਸਹੀ ਰਹੇ'
  },
  'landing.customer.feature3': {
    en: 'Receive updates directly from your tailor, keeping you informed every step of the way',
    hi: 'अपने दर्जी से सीधे अपडेट प्राप्त करें, जो आपको हर कदम पर सूचित रखता है',
    pa: 'ਆਪਣੇ ਦਰਜ਼ੀ ਤੋਂ ਸਿੱਧੇ ਅਪਡੇਟ ਪ੍ਰਾਪਤ ਕਰੋ, ਜੋ ਤੁਹਾਨੂੰ ਹਰ ਕਦਮ ਤੇ ਜਾਣਕਾਰੀ ਦਿੰਦਾ ਹੈ'
  },
  'landing.customer.feature4': {
    en: 'And because your trust matters, everything is backed by enterprise-grade security to protect your personal details and measurements',
    hi: 'और क्योंकि आपका विश्वास मायने रखता है, सब कुछ आपके व्यक्तिगत विवरण और मापों की सुरक्षा के लिए एंटरप्राइज-ग्रेड सुरक्षा द्वारा समर्थित है',
    pa: 'ਅਤੇ ਕਿਉਂਕਿ ਤੁਹਾਡਾ ਭਰੋਸਾ ਮਾਇਨੇ ਰੱਖਦਾ ਹੈ, ਸਭ ਕੁਝ ਤੁਹਾਡੇ ਨਿੱਜੀ ਵੇਰਵਿਆਂ ਅਤੇ ਮਾਪਾਂ ਦੀ ਸੁਰੱਖਿਆ ਲਈ ਐਂਟਰਪ੍ਰਾਈਜ਼-ਗ੍ਰੇਡ ਸੁਰੱਖਿਆ ਦੁਆਰਾ ਸਮਰਥਿਤ ਹੈ'
  },
  'landing.business.feature1': {
    en: 'All-in-one CRM and order management system to organize customers, measurements, and orders in one place',
    hi: 'ग्राहकों, मापों और ऑर्डर को एक जगह व्यवस्थित करने के लिए ऑल-इन-वन CRM और ऑर्डर प्रबंधन सिस्टम',
    pa: 'ਗਾਹਕਾਂ, ਮਾਪਾਂ ਅਤੇ ਆਰਡਰਾਂ ਨੂੰ ਇੱਕ ਜਗ੍ਹਾ ਵਿੱਚ ਵਿਵਸਥਿਤ ਕਰਨ ਲਈ ਆਲ-ਇਨ-ਵਨ CRM ਅਤੇ ਆਰਡਰ ਪ੍ਰਬੰਧਨ ਸਿਸਟਮ'
  },
  'landing.business.feature2': {
    en: 'Data-driven insights and reports to understand performance and make smarter business decisions',
    hi: 'प्रदर्शन को समझने और स्मार्ट व्यावसायिक निर्णय लेने के लिए डेटा-संचालित अंतर्दृष्टि और रिपोर्ट',
    pa: 'ਪ੍ਰਦਰਸ਼ਨ ਨੂੰ ਸਮਝਣ ਅਤੇ ਸਮਾਰਟ ਵਪਾਰੀ ਫੈਸਲੇ ਲੈਣ ਲਈ ਡੇਟਾ-ਆਧਾਰਿਤ ਸੂਝਾਂ ਅਤੇ ਰਿਪੋਰਟਾਂ'
  },
  'landing.business.feature3': {
    en: 'Seamless communication tools to keep customers updated and build lasting relationships',
    hi: 'ग्राहकों को अपडेट रखने और स्थायी संबंध बनाने के लिए निर्बाध संचार उपकरण',
    pa: 'ਗਾਹਕਾਂ ਨੂੰ ਅਪਡੇਟ ਰੱਖਣ ਅਤੇ ਸਥਾਈ ਸੰਬੰਧ ਬਣਾਉਣ ਲਈ ਨਿਰਬਾਧ ਸੰਚਾਰ ਟੂਲ'
  },
  'landing.business.feature4': {
    en: 'Growth-focused features to scale your tailoring business and reach more customers over time',
    hi: 'समय के साथ अपने दर्जी व्यवसाय को बढ़ाने और अधिक ग्राहकों तक पहुंचने के लिए विकास-केंद्रित सुविधाएं',
    pa: 'ਸਮੇਂ ਦੇ ਨਾਲ ਆਪਣੇ ਦਰਜ਼ੀ ਵਪਾਰ ਨੂੰ ਵਧਾਉਣ ਅਤੇ ਹੋਰ ਗਾਹਕਾਂ ਤੱਕ ਪਹੁੰਚਣ ਲਈ ਵਿਕਾਸ-ਕੇਂਦਰਿਤ ਵਿਸ਼ੇਸ਼ਤਾਵਾਂ'
  },
  'landing.business.closing': {
    en: 'With Master Ji, you get more than just software — you get a partner built to help tailors and designers thrive in the modern fashion world.',
    hi: 'मास्टर जी के साथ, आपको सिर्फ सॉफ्टवेयर नहीं मिलता — आपको एक साथी मिलता है जो दर्जियों और डिजाइनरों को आधुनिक फैशन दुनिया में फलने-फूलने में मदद करने के लिए बनाया गया है।',
    pa: 'ਮਾਸਟਰ ਜੀ ਦੇ ਨਾਲ, ਤੁਹਾਨੂੰ ਸਿਰਫ ਸਾਫਟਵੇਅਰ ਨਹੀਂ ਮਿਲਦਾ — ਤੁਹਾਨੂੰ ਇੱਕ ਸਾਥੀ ਮਿਲਦਾ ਹੈ ਜੋ ਦਰਜ਼ੀਆਂ ਅਤੇ ਡਿਜ਼ਾਈਨਰਾਂ ਨੂੰ ਆਧੁਨਿਕ ਫੈਸ਼ਨ ਦੁਨੀਆ ਵਿੱਚ ਫਲਣ-ਫੁੱਲਣ ਵਿੱਚ ਮਦਦ ਕਰਨ ਲਈ ਬਣਾਇਆ ਗਿਆ ਹੈ।'
  },
  'landing.customer.feature1.title': {
    en: 'Find Skilled Tailors',
    hi: 'कुशल दर्जी खोजें',
    pa: 'ਕੁਸ਼ਲ ਦਰਜ਼ੀ ਲੱਭੋ'
  },
  'landing.customer.feature2.title': {
    en: 'Easy Ordering',
    hi: 'आसान ऑर्डरिंग',
    pa: 'ਆਸਾਨ ਆਰਡਰਿੰਗ'
  },
  'landing.customer.feature3.title': {
    en: 'Secure & Private',
    hi: 'सुरक्षित और निजी',
    pa: 'ਸੁਰੱਖਿਅਤ ਅਤੇ ਨਿਜੀ'
  },
  'landing.business.feature1.title': {
    en: 'Business Management',
    hi: 'व्यवसाय प्रबंधन',
    pa: 'ਵਪਾਰ ਪ੍ਰਬੰਧਨ'
  },
  'landing.business.feature2.title': {
    en: 'Analytics & Insights',
    hi: 'विश्लेषण और अंतर्दृष्टि',
    pa: 'ਵਿਸ਼ਲੇਸ਼ਣ ਅਤੇ ਸੂਝਾਂ'
  },
  'landing.business.feature3.title': {
    en: 'Scale & Grow',
    hi: 'स्केल और बढ़ें',
    pa: 'ਸਕੇਲ ਅਤੇ ਵਧੋ'
  },

  // Measurement Fields
  'measurement.chest': {
    en: 'Chest',
    hi: 'छाती',
    pa: 'ਛਾਤੀ'
  },
  'measurement.waist': {
    en: 'Waist',
    hi: 'कमर',
    pa: 'ਕਮਰ'
  },
  'measurement.hips': {
    en: 'Hips',
    hi: 'कूल्हे',
    pa: 'ਹਿੱਪ'
  },
  'measurement.shoulder': {
    en: 'Shoulder',
    hi: 'कंधा',
    pa: 'ਮੋਢਾ'
  },
  'measurement.shoulder_width': {
    en: 'Shoulder Width',
    hi: 'कंधे की चौड़ाई',
    pa: 'ਤੀਰਾ'
  },
  'measurement.sleeve_length': {
    en: 'Sleeve Length',
    hi: 'आस्तीन की लंबाई',
    pa: 'ਬਾਂਹ'
  },
  'measurement.sleeve_width': {
    en: 'Sleeve Width',
    hi: 'आस्तीन की चौड़ाई',
    pa: 'ਬਾਂਹ ਦੀ ਚੌੜਾਈ'
  },
  'measurement.neck': {
    en: 'Neck',
    hi: 'गर्दन',
    pa: 'ਕਾਲਰ'
  },
  'measurement.inseam': {
    en: 'Inseam',
    hi: 'अंदर की सीम',
    pa: 'ਅੰਦਰੂਨੀ ਸੀਮ'
  },
  'measurement.height': {
    en: 'Height',
    hi: 'ऊंचाई',
    pa: 'ਲੰਬਾਈ'
  },
  'measurement.weight': {
    en: 'Weight',
    hi: 'वजन',
    pa: 'ਭਾਰ'
  },
  'measurement.bicep': {
    en: 'Bicep',
    hi: 'बाइसेप',
    pa: 'ਬਾਂਹ'
  },
  'measurement.forearm': {
    en: 'Forearm',
    hi: 'बांह',
    pa: 'ਬਾਂਹ'
  },
  'measurement.thigh': {
    en: 'Thigh',
    hi: 'जांघ',
    pa: 'ਪੱਟ'
  },
  'measurement.calf': {
    en: 'Calf',
    hi: 'पिंडली',
    pa: 'ਪਿੰਨੀ'
  },
  'measurement.ankle': {
    en: 'Ankle',
    hi: 'टखना',
    pa: 'ਗਿਦਰੀ'
  },
  'measurement.wrist': {
    en: 'Wrist',
    hi: 'कलाई',
    pa: 'ਕਲਾਈ'
  },
  'measurement.back_length': {
    en: 'Back Length',
    hi: 'पीठ की लंबाई',
    pa: 'ਬੈਕ'
  },
  'measurement.armhole': {
    en: 'Armhole',
    hi: 'बांह का छेद',
    pa: 'ਬਾਂਹ ਦਾ ਛੇਦ'
  },
  'measurement.crotch': {
    en: 'Crotch',
    hi: 'क्रॉच',
    pa: 'ਕ੍ਰੋਚ'
  },
  'measurement.outseam': {
    en: 'Outseam',
    hi: 'बाहरी सीम',
    pa: 'ਬਾਹਰੀ ਸੀਮ'
  },
  'measurement.bust': {
    en: 'Bust',
    hi: 'बस्ट',
    pa: 'ਛਾਤੀ'
  },
  'measurement.under_bust': {
    en: 'Under Bust',
    hi: 'बस्ट के नीचे',
    pa: 'ਛਾਤੀ ਹੇਠਾਂ'
  },
  'measurement.arm_length': {
    en: 'Arm Length',
    hi: 'बांह की लंबाई',
    pa: 'ਬਾਂਹ'
  },
  'measurement.elbow': {
    en: 'Elbow',
    hi: 'कोहनी',
    pa: 'ਕੂਹਣੀ'
  },
  'measurement.hem': {
    en: 'Hem',
    hi: 'हेम',
    pa: 'ਘੇਰਾ'
  },
  'measurement.knee': {
    en: 'Knee',
    hi: 'घुटना',
    pa: 'ਗੋਡਾ'
  },
  'measurement.cuff': {
    en: 'Cuff',
    hi: 'कफ',
    pa: 'ਮੂਹਰੀ'
  },
  'measurement.upper_length': {
    en: 'Upper Length',
    hi: 'ऊपरी लंबाई',
    pa: 'ਉੱਪਰੀ ਲੰਬਾਈ'
  },
  'measurement.upper_waist': {
    en: 'U-Waist',
    hi: 'ऊपरी कमर',
    pa: 'ਉੱਪਰੀ ਕਮਰ'
  },
  'measurement.upper_hip': {
    en: 'U-Hip',
    hi: 'ऊपरी हिप',
    pa: 'ਉੱਪਰੀ ਹਿੱਪ'
  },
  'measurement.front_length': {
    en: 'Front Length',
    hi: 'सामने की लंबाई',
    pa: 'ਅੱਗੇ ਦੀ ਲੰਬਾਈ'
  },
  'measurement.center_back': {
    en: 'Center Back',
    hi: 'सेंटर बैक',
    pa: 'ਸੈਂਟਰ ਬੈਕ'
  },
  'measurement.pant_length': {
    en: 'Pant Length',
    hi: 'पैंट की लंबाई',
    pa: 'ਪੈਂਟ ਦੀ ਲੰਬਾਈ'
  },
  // Concise Measurement Labels
  'measurement.concise.neck': {
    en: 'Neck',
    hi: 'गला',
    pa: 'ਗਰਦਨ'
  },
  'measurement.concise.shoulder': {
    en: 'Shoulder',
    hi: 'कंधा',
    pa: 'ਮੋਢਾ'
  },
  'measurement.concise.chest': {
    en: 'Chest',
    hi: 'छाती',
    pa: 'ਛਾਤੀ'
  },
  'measurement.concise.upper_waist': {
    en: 'U-Waist',
    hi: 'ऊपरी कमर',
    pa: 'ਉੱਪਰੀ ਕਮਰ'
  },
  'measurement.concise.upper_hip': {
    en: 'U-Hip',
    hi: 'ऊपरी हिप',
    pa: 'ਉੱਪਰੀ ਹਿੱਪ'
  },
  'measurement.concise.sleeve': {
    en: 'Sleeve',
    hi: 'आस्तीन',
    pa: 'ਬਾਂਹ'
  },
  'measurement.concise.armhole': {
    en: 'Armhole',
    hi: 'आर्महोल',
    pa: 'ਬਾਂਹ ਦਾ ਛੇਦ'
  },
  'measurement.concise.bicep': {
    en: 'Bicep',
    hi: 'बाइसेप',
    pa: 'ਬਾਈਸੈਪ'
  },
  'measurement.concise.forearm': {
    en: 'Forearm',
    hi: 'फोरआर्म',
    pa: 'ਬਾਂਹ'
  },
  'measurement.concise.wrist': {
    en: 'Wrist',
    hi: 'कलाई',
    pa: 'ਕਲਾਈ'
  },
  'measurement.concise.back': {
    en: 'Back',
    hi: 'पीठ',
    pa: 'ਪੀਠ'
  },
  'measurement.concise.top_length': {
    en: 'Top Length',
    hi: 'टॉप लंबाई',
    pa: 'ਟਾਪ ਲੰਬਾਈ'
  },
  'measurement.concise.waist': {
    en: 'Waist',
    hi: 'कमर',
    pa: 'ਕਮਰ'
  },
  'measurement.concise.hip': {
    en: 'Hip',
    hi: 'कूल्हा',
    pa: 'ਹਿੱਪ'
  },
  'measurement.concise.thigh': {
    en: 'Thigh',
    hi: 'जांघ',
    pa: 'ਜਾਂਘ'
  },
  'measurement.concise.knee': {
    en: 'Knee',
    hi: 'घुटना',
    pa: 'ਗੋਡਾ'
  },
  'measurement.concise.calf': {
    en: 'Calf',
    hi: 'पिंडली',
    pa: 'ਪਿੰਡਲੀ'
  },
  'measurement.concise.bottom': {
    en: 'Bottom',
    hi: 'नीचा',
    pa: 'ਹੇਠਲਾ'
  },
  'measurement.concise.crotch': {
    en: 'Crotch',
    hi: 'क्रॉच',
    pa: 'ਕ੍ਰੋਚ'
  },
  'measurement.concise.inseam': {
    en: 'Inseam',
    hi: 'भीतरी सीम',
    pa: 'ਅੰਦਰੂਨੀ ਸੀਮ'
  },
  'measurement.concise.length': {
    en: 'Length',
    hi: 'लंबाई',
    pa: 'ਲੰਬਾਈ'
  },
  'measurement.concise.height': {
    en: 'Height',
    hi: 'ऊंचाई',
    pa: 'ਕੱਦ'
  },
  'measurement.concise.weight': {
    en: 'Weight',
    hi: 'वजन',
    pa: 'ਭਾਰ'
  },
  'measurement.bottom': {
    en: 'Bottom',
    hi: 'नीचे',
    pa: 'ਹੇਠਾਂ'
  },
  'measurement.pet': {
    en: 'Stomach',
    hi: 'पेट',
    pa: 'ਪੇਟ'
  },
  'measurement.front': {
    en: 'Front',
    hi: 'सामने',
    pa: 'ਫਰੰਟ'
  },
  'measurement.back': {
    en: 'Back',
    hi: 'पीछे',
    pa: 'ਬੈਕ'
  },
  'measurement.date': {
    en: 'Date',
    hi: 'तारीख',
    pa: 'ਮਿਤੀ'
  },
  'measurement.number': {
    en: 'Number',
    hi: 'संख्या',
    pa: 'ਨੰ.'
  },
  'measurement.upper_body': {
    en: 'Upper Body Measurements',
    hi: 'ऊपरी शरीर के माप',
    pa: 'ਉਪਰਲੇ ਸਰੀਰ ਦੇ ਮਾਪ'
  },
  'measurement.lower_body': {
    en: 'Lower Body Measurements',
    hi: 'निचले शरीर के माप',
    pa: 'ਹੇਠਲੇ ਸਰੀਰ ਦੇ ਮਾਪ'
  },
  'measurement.additional': {
    en: 'Additional Measurements',
    hi: 'अतिरिक्त माप',
    pa: 'ਵਾਧੂ ਮਾਪ'
  },
  'measurement.inches': {
    en: 'inches',
    hi: 'इंच',
    pa: 'ਇੰਚ'
  },

  // Homepage Hero Section
  'home.hero.tagline': {
    en: 'Stitch. Manage. Grow.',
    hi: 'सिलाई। प्रबंधन। विकास।',
    pa: 'ਸਿਲਾਈ। ਪ੍ਰਬੰਧਨ। ਵਿਕਾਸ।',
    bn: 'সেলাই। পরিচালনা। বৃদ্ধি।',
    te: 'కుట్టు। నిర్వహణ। పెంపు।',
    ta: 'தைப்பு। நிர்வாகம்। வளர்ச்சி।',
    ml: 'തുന്നൽ। നിയന്ത്രണം। വളർച്ച।',
    kn: 'ಹೊಲಿಗೆ। ನಿರ್ವಹಣೆ। ಬೆಳವಣಿಗೆ।',
    gu: 'સીવણ। વ્યવસ્થાપન। વૃદ્ધિ।',
    mr: 'शिवण। व्यवस्थापन। वाढ।'
  },
  'home.hero.title': {
    en: 'Master Ji',
    hi: 'मास्टर जी',
    pa: 'ਮਾਸਟਰ ਜੀ',
    bn: 'মাস্টার জি',
    te: 'మాస్టర్ జీ',
    ta: 'மாஸ்டர் ஜி',
    ml: 'മാസ്റ്റർ ജി',
    kn: 'ಮಾಸ್ಟರ್ ಜಿ',
    gu: 'માસ્ટર જી',
    mr: 'मास्टर जी'
  },
  'home.hero.description.p1': {
    en: 'We are the dedicated space where custom craftsmanship meets modern simplicity.',
    hi: 'हम वह समर्पित स्थान हैं जहां कस्टम कारीगरी आधुनिक सादगी से मिलती है।',
    pa: 'ਅਸੀਂ ਉਹ ਸਮਰਪਿਤ ਸਥਾਨ ਹਾਂ ਜਿੱਥੇ ਕਸਟਮ ਕਾਰੀਗਰੀ ਆਧੁਨਿਕ ਸਾਦਗੀ ਨਾਲ ਮਿਲਦੀ ਹੈ।',
    bn: 'আমরা সেই নিবেদিত স্থান যেখানে কাস্টম কারুশিল্প আধুনিক সরলতার সাথে মিলিত হয়।',
    te: 'మేము అంకితమైన స్థలం, ఇక్కడ కస్టమ్ కారిగరీ ఆధునిక సరళతను కలుస్తుంది।',
    ta: 'நாங்கள் அர்ப்பணிக்கப்பட்ட இடம், அங்கு தனிப்பயன் கைவினைத்திறன் நவீன எளிமையை சந்திக்கிறது।',
    ml: 'കസ്റ്റം കരകൗശലം ആധുനിക ലാളിത്യവുമായി കണ്ടുമുട്ടുന്ന സമർപ്പിത സ്ഥലമാണ് ഞങ്ങൾ।',
    kn: 'ಕಸ್ಟಮ್ ಕರಕೌಶಲವು ಆಧುನಿಕ ಸರಳತೆಯನ್ನು ಭೇಟಿ ಮಾಡುವ ಸಮರ್ಪಿತ ಸ್ಥಳವಾಗಿದೆ ನಾವು।',
    gu: 'અમે તે સમર્પિત સ્થાન છીએ જ્યાં કસ્ટમ કારીગરી આધુનિક સરળતાને મળે છે।',
    mr: 'आम्ही ते समर्पित स्थान आहोत जेथे कस्टम कारागिरी आधुनिक साधेपणासह भेटते।'
  },
  'home.hero.description.p2': {
    en: 'Creators get the powerful tools to manage and grow their artistry. Customers get a single, free, seamless platform to track orders and manage their measurements.',
    hi: 'रचनाकारों को अपनी कला का प्रबंधन और विकास करने के लिए शक्तिशाली उपकरण मिलते हैं। ग्राहकों को ऑर्डर ट्रैक करने और अपने मापों को प्रबंधित करने के लिए एक एकल, मुफ्त, निर्बाध प्लेटफॉर्म मिलता है।',
    pa: 'ਰਚਨਾਕਾਰਾਂ ਨੂੰ ਆਪਣੀ ਕਲਾ ਦਾ ਪ੍ਰਬੰਧਨ ਅਤੇ ਵਿਕਾਸ ਕਰਨ ਲਈ ਸ਼ਕਤੀਸ਼ਾਲੀ ਟੂਲ ਮਿਲਦੇ ਹਨ। ਗਾਹਕਾਂ ਨੂੰ ਆਰਡਰ ਟ੍ਰੈਕ ਕਰਨ ਅਤੇ ਆਪਣੇ ਮਾਪਾਂ ਨੂੰ ਪ੍ਰਬੰਧਿਤ ਕਰਨ ਲਈ ਇੱਕ ਇਕਹਿਰਾ, ਮੁਫਤ, ਨਿਰਬਾਧ ਪਲੇਟਫਾਰਮ ਮਿਲਦਾ ਹੈ।',
    bn: 'স্রষ্টারা তাদের শিল্প পরিচালনা এবং বৃদ্ধি করার জন্য শক্তিশালী সরঞ্জাম পায়। গ্রাহকরা অর্ডার ট্র্যাক করতে এবং তাদের পরিমাপ পরিচালনা করার জন্য একটি একক, বিনামূল্যে, নিরবচ্ছিন্ন প্ল্যাটফর্ম পায়।',
    te: 'సృష్టికర్తలు తమ కళను నిర్వహించడానికి మరియు పెంచడానికి శక్తివంతమైన సాధనాలను పొందుతారు। కస్టమర్లు ఆర్డర్లను ట్రాక్ చేయడానికి మరియు వారి కొలతలను నిర్వహించడానికి ఒకే, ఉచిత, నిరవధిక ప్లాట్ఫారమ్ను పొందుతారు।',
    ta: 'படைப்பாளிகள் தங்கள் கலைத்திறனை நிர்வகிக்கவும் வளர்க்கவும் சக்திவாய்ந்த கருவிகளைப் பெறுகிறார்கள்। வாடிக்கையாளர்கள் ஆர்டர்களைக் கண்காணிக்கவும் தங்கள் அளவீடுகளை நிர்வகிக்கவும் ஒரு ஒற்றை, இலவச, தடையற்ற தளத்தைப் பெறுகிறார்கள்।',
    ml: 'സ്രഷ്ടാക്കൾക്ക് അവരുടെ കലയെ നിയന്ത്രിക്കാനും വളർത്താനും ശക്തമായ ഉപകരണങ്ങൾ ലഭിക്കുന്നു。 കസ്റ്റമർമാർക്ക് ഓർഡറുകൾ ട്രാക്ക് ചെയ്യാനും അവരുടെ അളവുകൾ നിയന്ത്രിക്കാനും ഒരൊറ്റ, സൗജന്യ, തടസ്സമില്ലാത്ത പ്ലാറ്റ്ഫോം ലഭിക്കുന്നു।',
    kn: 'ಸೃಷ್ಟಿಕರ್ತರು ತಮ್ಮ ಕಲೆಯನ್ನು ನಿರ್ವಹಿಸಲು ಮತ್ತು ಬೆಳೆಸಲು ಶಕ್ತಿಶಾಲಿ ಉಪಕರಣಗಳನ್ನು ಪಡೆಯುತ್ತಾರೆ। ಗ್ರಾಹಕರು ಆರ್ಡರ್ಗಳನ್ನು ಟ್ರ್ಯಾಕ್ ಮಾಡಲು ಮತ್ತು ಅವರ ಅಳತೆಗಳನ್ನು ನಿರ್ವಹಿಸಲು ಒಂದೇ, ಉಚಿತ, ನಿರಂತರ ವೇದಿಕೆಯನ್ನು ಪಡೆಯುತ್ತಾರೆ।',
    gu: 'સર્જકોને તેમની કલાને વ્યવસ્થાપિત કરવા અને વિકસાવવા માટે શક્તિશાળી સાધનો મળે છે। ગ્રાહકોને ઓર્ડર ટ્રેક કરવા અને તેમના માપનો વ્યવસ્થાપન કરવા માટે એક જ, મફત, નિરંતર પ્લેટફોર્મ મળે છે।',
    mr: 'निर्मात्यांना त्यांच्या कलेचे व्यवस्थापन आणि वाढवण्यासाठी शक्तिशाली साधने मिळतात। ग्राहकांना ऑर्डर ट्रॅक करण्यासाठी आणि त्यांच्या मोजमापांचे व्यवस्थापन करण्यासाठी एकच, विनामूल्य, निर्बाध प्लॅटफॉर्म मिळतो।'
  },
  'home.hero.description.p3': {
    en: 'Preserving tradition. Perfecting the process.',
    hi: 'परंपरा को संरक्षित करना। प्रक्रिया को परिपूर्ण बनाना।',
    pa: 'ਪਰੰਪਰਾ ਨੂੰ ਸੁਰੱਖਿਅਤ ਰੱਖਣਾ। ਪ੍ਰਕਿਰਿਆ ਨੂੰ ਸੰਪੂਰਨ ਬਣਾਉਣਾ।',
    bn: 'ঐতিহ্য সংরক্ষণ করা। প্রক্রিয়াকে নিখুঁত করা।',
    te: 'సంప్రదాయాన్ని సంరక్షించడం। ప్రక్రియను పరిపూర్ణం చేయడం।',
    ta: 'பாரம்பரியத்தை பாதுகாத்தல்। செயல்முறையை முழுமையாக்குதல்।',
    ml: 'പാരമ്പര്യം സംരക്ഷിക്കുന്നു। പ്രക്രിയയെ പൂർണ്ണമാക്കുന്നു।',
    kn: 'ಸಂಪ್ರದಾಯವನ್ನು ಸಂರಕ್ಷಿಸುವುದು। ಪ್ರಕ್ರಿಯೆಯನ್ನು ಪರಿಪೂರ್ಣಗೊಳಿಸುವುದು।',
    gu: 'પરંપરાને સંરક્ષણ કરવી। પ્રક્રિયાને પરિપૂર્ણ બનાવવી।',
    mr: 'परंपरा जतन करणे। प्रक्रिया परिपूर्ण करणे।'
  },
  'home.hero.cta.get_started': {
    en: 'Get Started',
    hi: 'शुरू करें',
    pa: 'ਸ਼ੁਰੂ ਕਰੋ',
    bn: 'শুরু করুন',
    te: 'ప్రారంభించండి',
    ta: 'தொடங்கவும்',
    ml: 'ആരംഭിക്കുക',
    kn: 'ಪ್ರಾರಂಭಿಸಿ',
    gu: 'શરૂ કરો',
    mr: 'सुरू करा'
  },
  'home.hero.cta.learn_more': {
    en: 'Learn more',
    hi: 'और जानें',
    pa: 'ਹੋਰ ਜਾਣੋ',
    bn: 'আরও জানুন',
    te: 'మరింత తెలుసుకోండి',
    ta: 'மேலும் அறிக',
    ml: 'കൂടുതൽ അറിയുക',
    kn: 'ಹೆಚ್ಚು ತಿಳಿಯಿರಿ',
    gu: 'વધુ જાણો',
    mr: 'अधिक जाणून घ्या'
  },
  'home.hero.stats.eliminate.title': {
    en: 'Eliminate',
    hi: 'खत्म करें',
    pa: 'ਖਤਮ ਕਰੋ',
    bn: 'দূর করুন',
    te: 'తొలగించండి',
    ta: 'நீக்கு',
    ml: 'ഇല്ലാതാക്കുക',
    kn: 'ತೆಗೆದುಹಾಕಿ',
    gu: 'દૂર કરો',
    mr: 'दूर करा'
  },
  'home.hero.stats.eliminate.description': {
    en: 'of time spent on paper-based filing and archive management.',
    hi: 'कागज-आधारित फाइलिंग और संग्रह प्रबंधन पर खर्च किए गए समय का।',
    pa: 'ਕਾਗਜ਼-ਅਧਾਰਿਤ ਫਾਈਲਿੰਗ ਅਤੇ ਅਭਿਲੇਖ ਪ੍ਰਬੰਧਨ \'ਤੇ ਖਰਚੇ ਗਏ ਸਮੇਂ ਦਾ।',
    bn: 'কাগজ-ভিত্তিক ফাইলিং এবং আর্কাইভ ব্যবস্থাপনায় ব্যয় করা সময়ের।',
    te: 'కాగితం-ఆధారిత ఫైలింగ్ మరియు ఆర్కైవ్ నిర్వహణలో ఖర్చు చేసిన సమయం।',
    ta: 'காகித-அடிப்படையிலான கோப்பு மற்றும் காப்பக மேலாண்மையில் செலவழித்த நேரத்தின்।',
    ml: 'കടലാസ്-അടിസ്ഥാന ഫയലിംഗ്, ആർക്കൈവ് മാനേജ്മെന്റിൽ ചെലവഴിച്ച സമയത്തിന്റെ।',
    kn: 'ಕಾಗದ-ಆಧಾರಿತ ಫೈಲಿಂಗ್ ಮತ್ತು ಆರ್ಕೈವ್ ನಿರ್ವಹಣೆಯಲ್ಲಿ ಖರ್ಚು ಮಾಡಿದ ಸಮಯದ।',
    gu: 'કાગળ-આધારિત ફાઇલિંગ અને આર્કાઇવ મેનેજમેન્ટ પર ખર્ચ કરેલા સમયનું।',
    mr: 'कागद-आधारित फाइलिंग आणि आर्काइव्ह व्यवस्थापनावर खर्च केलेल्या वेळेचे।'
  },
  'home.hero.stats.time_saved.title': {
    en: 'Time Saved on Order Intake',
    hi: 'ऑर्डर इनटेक पर बचाया गया समय',
    pa: 'ਆਰਡਰ ਇਨਟੇਕ \'ਤੇ ਬਚਾਇਆ ਗਿਆ ਸਮਾਂ',
    bn: 'অর্ডার গ্রহণে সাশ্রয়ী সময়',
    te: 'ఆర్డర్ ఇన్టేక్‌లో ఆదా చేసిన సమయం',
    ta: 'ஆர்டர் உள்ளீட்டில் சேமிக்கப்பட்ட நேரம்',
    ml: 'ഓർഡർ ഇൻടേക്കിൽ സംരക്ഷിച്ച സമയം',
    kn: 'ಆರ್ಡರ್ ಇನ್ಟೇಕ್‌ನಲ್ಲಿ ಉಳಿಸಿದ ಸಮಯ',
    gu: 'ઓર્ડર ઇન્ટેક પર બચાવેલો સમય',
    mr: 'ऑर्डर इनटेकवर वाचवलेला वेळ'
  },

  // Homepage Our Story Section
  'home.story.badge': {
    en: 'Our Story',
    hi: 'हमारी कहानी',
    pa: 'ਸਾਡੀ ਕਹਾਣੀ',
    bn: 'আমাদের গল্প',
    te: 'మా కథ',
    ta: 'எங்கள் கதை',
    ml: 'ഞങ്ങളുടെ കഥ',
    kn: 'ನಮ್ಮ ಕಥೆ',
    gu: 'અમારી કથા',
    mr: 'आमची कथा'
  },
  'home.story.title': {
    en: 'Built on Heritage, Powered by Innovation',
    hi: 'विरासत पर निर्मित, नवाचार द्वारा संचालित',
    pa: 'ਵਿਰਾਸਤ \'ਤੇ ਬਣਾਇਆ, ਨਵੀਨਤਾ ਦੁਆਰਾ ਸੰਚਾਲਿਤ',
    bn: 'ঐতিহ্যের উপর নির্মিত, উদ্ভাবন দ্বারা চালিত',
    te: 'వారసత్వంపై నిర్మించబడింది, నవీకరణ ద్వారా శక్తివంతం',
    ta: 'பாரம்பரியத்தின் மீது கட்டப்பட்டது, புதுமையால் இயக்கப்படுகிறது',
    ml: 'പാരമ്പര്യത്തിൽ നിർമ്മിച്ചത്, നവീകരണത്താൽ പ്രവർത്തിക്കുന്നു',
    kn: 'ವಾರಸೆಯ ಮೇಲೆ ನಿರ್ಮಿಸಲಾಗಿದೆ, ನವೀಕರಣದಿಂದ ಶಕ್ತಿಯುತವಾಗಿದೆ',
    gu: 'વારસા પર બનેલું, નવીનતા દ્વારા સંચાલિત',
    mr: 'वारसावर बांधलेले, नवकल्पनेद्वारे चालवलेले'
  },
  'home.story.content.p1': {
    en: 'Master Ji is more than just software; it\'s the vision of two brothers.',
    hi: 'मास्टर जी सिर्फ सॉफ्टवेयर से कहीं अधिक है; यह दो भाइयों की दृष्टि है।',
    pa: 'ਮਾਸਟਰ ਜੀ ਸਿਰਫ ਸਾਫਟਵੇਅਰ ਤੋਂ ਵੱਧ ਹੈ; ਇਹ ਦੋ ਭਰਾਵਾਂ ਦੀ ਦ੍ਰਿਸ਼ਟੀ ਹੈ।',
    bn: 'মাস্টার জি শুধু সফ্টওয়্যার নয়; এটি দুটি ভাইয়ের দৃষ্টিভঙ্গি।',
    te: 'మాస్టర్ జీ కేవలం సాఫ్ట్‌వేర్ కంటే ఎక్కువ; ఇది రెండు సోదరుల దృష్టి।',
    ta: 'மாஸ்டர் ஜி என்பது மென்பொருளை விட அதிகமானது; இது இரண்டு சகோதரர்களின் பார்வை।',
    ml: 'മാസ്റ്റർ ജി സോഫ്റ്റ്‌വെയറിനേക്കാൾ കൂടുതലാണ്; ഇത് രണ്ട് സഹോദരന്മാരുടെ ദർശനമാണ്।',
    kn: 'ಮಾಸ್ಟರ್ ಜಿ ಕೇವಲ ಸಾಫ್ಟ್‌ವೇರ್ ಅಲ್ಲ; ಇದು ಇಬ್ಬರು ಸಹೋದರರ ದೃಷ್ಟಿ।',
    gu: 'માસ્ટર જી ફક્ત સોફ્ટવેર કરતાં વધુ છે; તે બે ભાઈઓની દ્રષ્ટિ છે।',
    mr: 'मास्टर जी हा फक्त सॉफ्टवेअरपेक्षा जास्त आहे; हे दोन भावांचे दृष्टीकोन आहे।'
  },
  'home.story.content.p2': {
    en: 'Growing up between the time-honored garment industry of Ludhiana, India, and the cutting-edge AI/data tech world of the USA, they identified a critical challenge: the bespoke craft was lacking digital tools.',
    hi: 'भारत के लुधियाना के समय-सम्मानित वस्त्र उद्योग और अमेरिका की अत्याधुनिक AI/डेटा तकनीकी दुनिया के बीच बढ़ते हुए, उन्होंने एक महत्वपूर्ण चुनौती की पहचान की: बेस्पोक शिल्प में डिजिटल उपकरणों की कमी थी।',
    pa: 'ਭਾਰਤ ਦੇ ਲੁਧਿਆਣਾ ਦੇ ਸਮੇਂ-ਸਨਮਾਨਿਤ ਕੱਪੜਾ ਉਦਯੋਗ ਅਤੇ ਅਮਰੀਕਾ ਦੀ ਅਤਿਅਧੁਨਿਕ AI/ਡੇਟਾ ਤਕਨਾਲੋਜੀ ਦੁਨੀਆ ਦੇ ਵਿਚਕਾਰ ਵੱਡੇ ਹੋਏ, ਉਨ੍ਹਾਂ ਨੇ ਇੱਕ ਮਹੱਤਵਪੂਰਨ ਚੁਣੌਤੀ ਦੀ ਪਛਾਣ ਕੀਤੀ: ਬੇਸਪੋਕ ਕਾਰੀਗਰੀ ਵਿੱਚ ਡਿਜੀਟਲ ਟੂਲਾਂ ਦੀ ਕਮੀ ਸੀ।',
    bn: 'ভারতের লুধিয়ানার সময়-সম্মানিত পোশাক শিল্প এবং মার্কিন যুক্তরাষ্ট্রের অত্যাধুনিক AI/ডেটা প্রযুক্তি জগতের মধ্যে বেড়ে ওঠা, তারা একটি গুরুত্বপূর্ণ চ্যালেঞ্জ চিহ্নিত করেছিল: বেস্পোক কারুশিল্পে ডিজিটাল সরঞ্জামের অভাব ছিল।',
    te: 'భారతదేశంలోని లుధియానా యొక్క సమయ-గౌరవించబడిన వస్త్ర పరిశ్రమ మరియు USA యొక్క అత్యాధునిక AI/డేటా టెక్ ప్రపంచం మధ్య పెరిగిన వారు, క్లిష్టమైన సవాలును గుర్తించారు: బెస్పోక్ క్రాఫ్ట్‌లో డిజిటల్ సాధనాల కొరత ఉంది।',
    ta: 'இந்தியாவின் லுதியானாவின் காலமான ஆடை தொழில்துறை மற்றும் USA இன் முன்னணி AI/டேட்டா டெக் உலகத்திற்கு இடையே வளர்ந்தவர்கள், ஒரு முக்கியமான சவாலை அடையாளம் கண்டனர்: பேஸ்போக் கைவினைத்திறனில் டிஜிட்டல் கருவிகள் குறைவாக இருந்தன।',
    ml: 'ഇന്ത്യയിലെ ലുധിയാനയുടെ സമയ-ബഹുമാനിക്കപ്പെട്ട വസ്ത്ര വ്യവസായവും USA യുടെ മുൻനിര AI/ഡാറ്റ ടെക് ലോകവും തമ്മിൽ വളർന്നവർ, ഒരു നിർണായക വെല്ലുവിളിയെ തിരിച്ചറിഞ്ഞു: ബെസ്പോക് ക്രാഫ്റ്റിൽ ഡിജിറ്റൽ ഉപകരണങ്ങളുടെ കുറവുണ്ടായിരുന്നു।',
    kn: 'ಭಾರತದ ಲುಧಿಯಾನಾದ ಸಮಯ-ಗೌರವಿಸಲ್ಪಟ್ಟ ವಸ್ತ್ರ ಉದ್ಯಮ ಮತ್ತು USA ಯ ಮುಂಚೂಣಿ AI/ಡೇಟಾ ಟೆಕ್ ಪ್ರಪಂಚದ ನಡುವೆ ಬೆಳೆದವರು, ನಿರ್ಣಾಯಕ ಸವಾಲನ್ನು ಗುರುತಿಸಿದರು: ಬೆಸ್ಪೋಕ್ ಕರಕೌಶಲದಲ್ಲಿ ಡಿಜಿಟಲ್ ಸಾಧನಗಳ ಕೊರತೆ ಇತ್ತು।',
    gu: 'ભારતના લુધિયાનાના સમય-સન્માનિત વસ્ત્ર ઉદ્યોગ અને USA ની અત્યાધુનિક AI/ડેટા ટેક વિશ્વ વચ્ચે વધીને, તેઓએ એક નિર્ણાયક પડકારને ઓળખ્યો: બેસ્પોક કારીગરીમાં ડિજિટલ સાધનોની ઉણપ હતી।',
    mr: 'भारतातील लुधियानाच्या काळ-सन्मानित वस्त्र उद्योग आणि USA च्या अत्याधुनिक AI/डेटा टेक जगात वाढलेले, त्यांनी एक निर्णायक आव्हान ओळखले: बेस्पोक कारागिरीत डिजिटल साधनांची कमतरता होती।'
  },
  'home.story.content.p3': {
    en: 'We combined that deep industry knowledge with modern technology to create Master Ji—a secure, efficient platform dedicated to preserving the artistry of tailoring while enabling creators to manage, scale, and thrive in the digital age.',
    hi: 'हमने उस गहरे उद्योग ज्ञान को आधुनिक प्रौद्योगिकी के साथ जोड़कर मास्टर जी बनाया—एक सुरक्षित, कुशल प्लेटफॉर्म जो टेलरिंग की कलात्मकता को संरक्षित करने के लिए समर्पित है, जबकि रचनाकारों को डिजिटल युग में प्रबंधन, स्केल और फलने-फूलने में सक्षम बनाता है।',
    pa: 'ਅਸੀਂ ਉਸ ਡੂੰਘੇ ਉਦਯੋਗ ਦੇ ਗਿਆਨ ਨੂੰ ਆਧੁਨਿਕ ਤਕਨਾਲੋਜੀ ਨਾਲ ਜੋੜ ਕੇ ਮਾਸਟਰ ਜੀ ਬਣਾਇਆ—ਇੱਕ ਸੁਰੱਖਿਤ, ਕੁਸ਼ਲ ਪਲੇਟਫਾਰਮ ਜੋ ਦਰਜ਼ੀ ਦੀ ਕਲਾਤਮਕਤਾ ਨੂੰ ਸੁਰੱਖਿਤ ਰੱਖਣ ਲਈ ਸਮਰਪਿਤ ਹੈ, ਜਦੋਂ ਕਿ ਰਚਨਾਕਾਰਾਂ ਨੂੰ ਡਿਜੀਟਲ ਯੁੱਗ ਵਿੱਚ ਪ੍ਰਬੰਧਨ, ਸਕੇਲ ਅਤੇ ਫਲਣ-ਫੁੱਲਣ ਵਿੱਚ ਸਮਰੱਥ ਬਣਾਉਂਦਾ ਹੈ।',
    bn: 'আমরা সেই গভীর শিল্প জ্ঞানকে আধুনিক প্রযুক্তির সাথে একত্রিত করে মাস্টার জি তৈরি করেছি—একটি নিরাপদ, দক্ষ প্ল্যাটফর্ম যা টেইলরিংয়ের শিল্পকে সংরক্ষণ করার জন্য নিবেদিত, যখন স্রষ্টাদের ডিজিটাল যুগে পরিচালনা, স্কেল এবং উন্নতি করতে সক্ষম করে।',
    te: 'మేము ఆ లోతైన పరిశ్రమ జ్ఞానాన్ని ఆధునిక సాంకేతికతతో కలిపి మాస్టర్ జీని సృష్టించాము—దర్జీ కళను సంరక్షించడానికి అంకితమైన ఒక సురక్షితమైన, సమర్థవంతమైన ప్లాట్‌ఫారమ్, అయితే సృష్టికర్తలను డిజిటల్ యుగంలో నిర్వహించడానికి, స్కేల్ చేయడానికి మరియు అభివృద్ధి చెందడానికి అనుమతిస్తుంది।',
    ta: 'நாங்கள் அந்த ஆழமான தொழில்துறை அறிவை நவீன தொழில்நுட்பத்துடன் இணைத்து மாஸ்டர் ஜியை உருவாக்கினோம்—தையலின் கலைத்திறனைப் பாதுகாக்க அர்ப்பணிக்கப்பட்ட பாதுகாப்பான, திறமையான தளம், அதே நேரத்தில் படைப்பாளிகளை டிஜிட்டல் யுகத்தில் நிர்வகிக்க, அளவிட மற்றும் செழிக்க அனுமதிக்கிறது।',
    ml: 'ആ ആഴത്തിലുള്ള വ്യവസായ അറിവിനെ ആധുനിക സാങ്കേതികവിദ്യയുമായി സംയോജിപ്പിച്ച് മാസ്റ്റർ ജി സൃഷ്ടിച്ചു—തയ്യൽ കലയെ സംരക്ഷിക്കാൻ സമർപ്പിച്ച ഒരു സുരക്ഷിതമായ, കാര്യക്ഷമമായ പ്ലാറ്റ്‌ഫോം, അതേസമയം സ്രഷ്ടാക്കളെ ഡിജിറ്റൽ യുഗത്തിൽ നിയന്ത്രിക്കാനും, സ്കെയിൽ ചെയ്യാനും, വളരാനും അനുവദിക്കുന്നു।',
    kn: 'ನಾವು ಆ ಆಳವಾದ ಉದ್ಯಮ ಜ್ಞಾನವನ್ನು ಆಧುನಿಕ ತಂತ್ರಜ್ಞಾನದೊಂದಿಗೆ ಸಂಯೋಜಿಸಿ ಮಾಸ್ಟರ್ ಜಿಯನ್ನು ಸೃಷ್ಟಿಸಿದ್ದೇವೆ—ತಯಾರಿಕೆಯ ಕಲೆಯನ್ನು ಸಂರಕ್ಷಿಸಲು ಅರ್ಪಿತವಾದ ಒಂದು ಸುರಕ್ಷಿತ, ಸಮರ್ಥ ಪ್ಲಾಟ್‌ಫಾರ್ಮ್, ಅದೇ ಸಮಯದಲ್ಲಿ ಸೃಷ್ಟಿಕರ್ತರನ್ನು ಡಿಜಿಟಲ್ ಯುಗದಲ್ಲಿ ನಿರ್ವಹಿಸಲು, ಸ್ಕೇಲ್ ಮಾಡಲು ಮತ್ತು ಅಭಿವೃದ್ಧಿ ಹೊಂದಲು ಅನುಮತಿಸುತ್ತದೆ।',
    gu: 'અમે તે ઊંડા ઉદ્યોગના જ્ઞાનને આધુનિક ટેકનોલોજી સાથે જોડીને માસ્ટર જી બનાવ્યું—દરજીની કલાને સંરક્ષણ કરવા માટે સમર્પિત એક સુરક્ષિત, કાર્યક્ષમ પ્લેટફોર્મ, જ્યારે સર્જકોને ડિજિટલ યુગમાં વ્યવસ્થાપન, સ્કેલ અને વિકાસ કરવા માટે સક્ષમ બનાવે છે।',
    mr: 'आम्ही त्या खोल उद्योगाच्या ज्ञानाला आधुनिक तंत्रज्ञानासह एकत्र करून मास्टर जी तयार केले—शिवणकामाच्या कलेचे संरक्षण करण्यासाठी समर्पित एक सुरक्षित, कार्यक्षम प्लॅटफॉर्म, तर निर्मात्यांना डिजिटल युगात व्यवस्थापन, स्केल आणि वाढ करण्यास सक्षम करते।'
  },
  'home.story.cta': {
    en: 'Read our Full Mission & Story',
    hi: 'हमारा पूरा मिशन और कहानी पढ़ें',
    pa: 'ਸਾਡਾ ਪੂਰਾ ਮਿਸ਼ਨ ਅਤੇ ਕਹਾਣੀ ਪੜ੍ਹੋ',
    bn: 'আমাদের সম্পূর্ণ মিশন এবং গল্প পড়ুন',
    te: 'మా పూర్తి మిషన్ మరియు కథ చదవండి',
    ta: 'எங்கள் முழு பணி மற்றும் கதையைப் படிக்கவும்',
    ml: 'ഞങ്ങളുടെ പൂർണ്ണ മിഷൻ, കഥ വായിക്കുക',
    kn: 'ನಮ್ಮ ಪೂರ್ಣ ಮಿಷನ್ ಮತ್ತು ಕಥೆಯನ್ನು ಓದಿ',
    gu: 'અમારું સંપૂર્ણ મિશન અને કથા વાંચો',
    mr: 'आमचे पूर्ण मिशन आणि कथा वाचा'
  },
  'home.story.founder.amandeep': {
    en: 'Amandeep Singh Nanda',
    hi: 'अमनदीप सिंह नंदा',
    pa: 'ਅਮਨਦੀਪ ਸਿੰਘ ਨੰਦਾ',
    bn: 'অমানদীপ সিং নন্দা',
    te: 'అమన్‌దీప్ సింగ్ నందా',
    ta: 'அமன்தீப் சிங் நந்தா',
    ml: 'അമൻ‌ദീപ് സിംഗ് നന്ദ',
    kn: 'ಅಮನ್‌ದೀಪ್ ಸಿಂಗ್ ನಂದಾ',
    gu: 'અમનદીપ સિંહ નંદા',
    mr: 'अमनदीप सिंग नंदा'
  },
  'home.story.founder.gurpreet': {
    en: 'Gurpreet Singh Nanda',
    hi: 'गुरप्रीत सिंह नंदा',
    pa: 'ਗੁਰਪ੍ਰੀਤ ਸਿੰਘ ਨੰਦਾ',
    bn: 'গুরপ্রীত সিং নন্দা',
    te: 'గుర్‌ప్రీత్ సింగ్ నందా',
    ta: 'குர்ப்ரீத் சிங் நந்தா',
    ml: 'ഗുർ‌പ്രീത് സിംഗ് നന്ദ',
    kn: 'ಗುರ್‌ಪ್ರೀತ್ ಸಿಂಗ್ ನಂದಾ',
    gu: 'ગુરપ્રીત સિંહ નંદા',
    mr: 'गुरप्रीत सिंग नंदा'
  },

  // Homepage How It Works Section
  'home.how_it_works.badge': {
    en: 'How it Works',
    hi: 'यह कैसे काम करता है',
    pa: 'ਇਹ ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ',
    bn: 'এটি কীভাবে কাজ করে',
    te: 'ఇది ఎలా పని చేస్తుంది',
    ta: 'இது எவ்வாறு செயல்படுகிறது',
    ml: 'ഇത് എങ്ങനെ പ്രവർത്തിക്കുന്നു',
    kn: 'ಇದು ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ',
    gu: 'આ કેવી રીતે કામ કરે છે',
    mr: 'हे कसे काम करते'
  },
  'home.how_it_works.title': {
    en: 'Simple Process. Powerful Results.',
    hi: 'सरल प्रक्रिया। शक्तिशाली परिणाम।',
    pa: 'ਸਰਲ ਪ੍ਰਕਿਰਿਆ। ਸ਼ਕਤੀਸ਼ਾਲੀ ਨਤੀਜੇ।',
    bn: 'সরল প্রক্রিয়া। শক্তিশালী ফলাফল।',
    te: 'సరళమైన ప్రక్రియ। శక్తివంతమైన ఫలితాలు।',
    ta: 'எளிய செயல்முறை। சக்திவாய்ந்த முடிவுகள்।',
    ml: 'ലളിതമായ പ്രക്രിയ। ശക്തമായ ഫലങ്ങൾ।',
    kn: 'ಸರಳ ಪ್ರಕ್ರಿಯೆ। ಶಕ್ತಿಶಾಲಿ ಫಲಿತಾಂಶಗಳು।',
    gu: 'સરળ પ્રક્રિયા। શક્તિશાળી પરિણામો।',
    mr: 'सोपी प्रक्रिया। शक्तिशाली परिणाम।'
  },
  'home.how_it_works.step1.title': {
    en: 'Place & Log',
    hi: 'स्थान और लॉग',
    pa: 'ਸਥਾਨ ਅਤੇ ਲੌਗ',
    bn: 'স্থান এবং লগ',
    te: 'స్థానం మరియు లాగ్',
    ta: 'இடம் மற்றும் பதிவு',
    ml: 'സ്ഥാനവും ലോഗും',
    kn: 'ಸ್ಥಳ ಮತ್ತು ಲಾಗ್',
    gu: 'સ્થાન અને લોગ',
    mr: 'स्थान आणि लॉग'
  },
  'home.how_it_works.step1.description': {
    en: 'Businesses can log orders and customers can view and track their orders',
    hi: 'व्यवसाय ऑर्डर लॉग कर सकते हैं और ग्राहक अपने ऑर्डर देख और ट्रैक कर सकते हैं',
    pa: 'ਵਪਾਰ ਆਰਡਰ ਲੌਗ ਕਰ ਸਕਦੇ ਹਨ ਅਤੇ ਗਾਹਕ ਆਪਣੇ ਆਰਡਰ ਦੇਖ ਅਤੇ ਟ੍ਰੈਕ ਕਰ ਸਕਦੇ ਹਨ',
    bn: 'ব্যবসা অর্ডার লগ করতে পারে এবং গ্রাহকরা তাদের অর্ডার দেখতে এবং ট্র্যাক করতে পারে',
    te: 'వ్యాపారాలు ఆర్డర్‌లను లాగ్ చేయగలవు మరియు కస్టమర్‌లు వారి ఆర్డర్‌లను వీక్షించవచ్చు మరియు ట్రాక్ చేయవచ్చు',
    ta: 'வணிகங்கள் ஆர்டர்களை பதிவு செய்யலாம் மற்றும் வாடிக்கையாளர்கள் தங்கள் ஆர்டர்களைப் பார்க்கலாம் மற்றும் கண்காணிக்கலாம்',
    ml: 'ബിസിനസുകൾ ഓർഡറുകൾ ലോഗ് ചെയ്യാനും ഉപഭോക്താക്കൾക്ക് അവരുടെ ഓർഡറുകൾ കാണാനും ട്രാക്ക് ചെയ്യാനും കഴിയും',
    kn: 'ವ್ಯವಹಾರಗಳು ಆರ್ಡರ್‌ಗಳನ್ನು ಲಾಗ್ ಮಾಡಬಹುದು ಮತ್ತು ಗ್ರಾಹಕರು ತಮ್ಮ ಆರ್ಡರ್‌ಗಳನ್ನು ವೀಕ್ಷಿಸಬಹುದು ಮತ್ತು ಟ್ರ್ಯಾಕ್ ಮಾಡಬಹುದು',
    gu: 'વ્યવસાયો ઓર્ડર લોગ કરી શકે છે અને ગ્રાહકો તેમના ઓર્ડર જોઈ અને ટ્રેક કરી શકે છે',
    mr: 'व्यवसाय ऑर्डर लॉग करू शकतात आणि ग्राहक त्यांचे ऑर्डर पाहू शकतात आणि ट्रॅक करू शकतात'
  },
  'home.how_it_works.step2.title': {
    en: 'Produce & Track',
    hi: 'उत्पादन और ट्रैक',
    pa: 'ਉਤਪਾਦਨ ਅਤੇ ਟ੍ਰੈਕ',
    bn: 'উৎপাদন এবং ট্র্যাক',
    te: 'ఉత్పత్తి మరియు ట్రాక్',
    ta: 'உற்பத்தி மற்றும் கண்காணிப்பு',
    ml: 'ഉൽപാദനവും ട്രാക്കും',
    kn: 'ಉತ್ಪಾದನೆ ಮತ್ತು ಟ್ರ್ಯಾಕ್',
    gu: 'ઉત્પાદન અને ટ્રેક',
    mr: 'उत्पादन आणि ट्रॅक'
  },
  'home.how_it_works.step2.description': {
    en: 'Master Ji offers tracking of orders as well as inventory and other business support',
    hi: 'मास्टर जी ऑर्डर के साथ-साथ इन्वेंटरी और अन्य व्यावसायिक सहायता की ट्रैकिंग प्रदान करता है',
    pa: 'ਮਾਸਟਰ ਜੀ ਆਰਡਰਾਂ ਦੀ ਟ੍ਰੈਕਿੰਗ ਦੇ ਨਾਲ-ਨਾਲ ਇਨਵੈਂਟਰੀ ਅਤੇ ਹੋਰ ਵਪਾਰਕ ਸਹਾਇਤਾ ਪ੍ਰਦਾਨ ਕਰਦਾ ਹੈ',
    bn: 'মাস্টার জি অর্ডার ট্র্যাকিংয়ের পাশাপাশি ইনভেন্টরি এবং অন্যান্য ব্যবসায়িক সহায়তা প্রদান করে',
    te: 'మాస్టర్ జీ ఆర్డర్‌ల ట్రాకింగ్‌తో పాటు ఇన్వెంటరీ మరియు ఇతర వ్యాపార మద్దతును అందిస్తుంది',
    ta: 'மாஸ்டர் ஜி ஆர்டர்களைக் கண்காணிப்பதுடன் சரக்கு மற்றும் பிற வணிக ஆதரவையும் வழங்குகிறது',
    ml: 'മാസ്റ്റർ ജി ഓർഡറുകളുടെ ട്രാക്കിംഗ് മാത്രമല്ല, ഇൻവെന്ററിയും മറ്റ് ബിസിനസ് പിന്തുണയും നൽകുന്നു',
    kn: 'ಮಾಸ್ಟರ್ ಜಿ ಆರ್ಡರ್‌ಗಳ ಟ್ರ್ಯಾಕಿಂಗ್‌ನೊಂದಿಗೆ ಸ್ಟಾಕ್ ಮತ್ತು ಇತರ ವ್ಯಾಪಾರ ಬೆಂಬಲವನ್ನು ಒದಗಿಸುತ್ತದೆ',
    gu: 'માસ્ટર જી ઓર્ડરની ટ્રેકિંગ સાથે સાથે ઇન્વેન્ટરી અને અન્ય વ્યવસાયિક સહાય પ્રદાન કરે છે',
    mr: 'मास्टर जी ऑर्डर ट्रॅकिंगसह इन्व्हेंटरी आणि इतर व्यावसायिक आधार प्रदान करते'
  },
  'home.how_it_works.step3.title': {
    en: 'Receive & Review',
    hi: 'प्राप्त करें और समीक्षा करें',
    pa: 'ਪ੍ਰਾਪਤ ਕਰੋ ਅਤੇ ਸਮੀਖਿਆ ਕਰੋ',
    bn: 'গ্রহণ করুন এবং পর্যালোচনা করুন',
    te: 'స్వీకరించండి మరియు సమీక్షించండి',
    ta: 'பெறவும் மதிப்பாய்வு செய்யவும்',
    ml: 'സ്വീകരിക്കുകയും അവലോകനം ചെയ്യുകയും ചെയ്യുക',
    kn: 'ಸ್ವೀಕರಿಸಿ ಮತ್ತು ವಿಮರ್ಶೆ ಮಾಡಿ',
    gu: 'પ્રાપ્ત કરો અને સમીક્ષા કરો',
    mr: 'प्राप्त करा आणि पुनरावलोकन करा'
  },
  'home.how_it_works.step3.description': {
    en: 'Upon completion, customers are able to rate and review tailors.',
    hi: 'पूरा होने पर, ग्राहक दर्जी को रेट और समीक्षा कर सकते हैं।',
    pa: 'ਪੂਰਾ ਹੋਣ \'ਤੇ, ਗਾਹਕ ਦਰਜ਼ੀਆਂ ਨੂੰ ਰੇਟ ਅਤੇ ਸਮੀਖਿਆ ਕਰ ਸਕਦੇ ਹਨ।',
    bn: 'সম্পূর্ণ হলে, গ্রাহকরা দর্জিদের রেটিং এবং পর্যালোচনা করতে পারেন।',
    te: 'పూర్తయిన తర్వాత, కస్టమర్‌లు దర్జీలను రేట్ చేయవచ్చు మరియు సమీక్షించవచ్చు।',
    ta: 'முடிந்ததும், வாடிக்கையாளர்கள் தையல்காரர்களை மதிப்பிடலாம் மற்றும் மதிப்பாய்வு செய்யலாம்।',
    ml: 'പൂർത്തിയാകുമ്പോൾ, ഉപഭോക്താക്കൾക്ക് ടെയ്ലറുകളെ റേറ്റ് ചെയ്യാനും അവലോകനം ചെയ്യാനും കഴിയും।',
    kn: 'ಪೂರ್ಣಗೊಂಡ ನಂತರ, ಗ್ರಾಹಕರು ಟೈಲರ್‌ಗಳನ್ನು ರೇಟ್ ಮಾಡಬಹುದು ಮತ್ತು ವಿಮರ್ಶೆ ಮಾಡಬಹುದು।',
    gu: 'પૂર્ણ થયા પછી, ગ્રાહકો ટેલરોને રેટ કરી શકે છે અને સમીક્ષા કરી શકે છે।',
    mr: 'पूर्ण झाल्यानंतर, ग्राहक टेलरचे रेटिंग आणि पुनरावलोकन करू शकतात।'
  },

  // Homepage Customers Section
  'home.customers.badge': {
    en: 'Securely Track Your Craftsmanship',
    hi: 'अपनी कारीगरी को सुरक्षित रूप से ट्रैक करें',
    pa: 'ਆਪਣੀ ਕਾਰੀਗਰੀ ਨੂੰ ਸੁਰੱਖਿਤ ਰੂਪ ਵਿੱਚ ਟ੍ਰੈਕ ਕਰੋ',
    bn: 'আপনার কারুশিল্প নিরাপদে ট্র্যাক করুন',
    te: 'మీ కరకౌశలాన్ని సురక్షితంగా ట్రాక్ చేయండి',
    ta: 'உங்கள் கைவினைத்திறனை பாதுகாப்பாக கண்காணிக்கவும்',
    ml: 'നിങ്ങളുടെ കരകൗശലം സുരക്ഷിതമായി ട്രാക്ക് ചെയ്യുക',
    kn: 'ನಿಮ್ಮ ಕರಕೌಶಲವನ್ನು ಸುರಕ್ಷಿತವಾಗಿ ಟ್ರ್ಯಾಕ್ ಮಾಡಿ',
    gu: 'તમારી કારીગરીને સુરક્ષિત રીતે ટ્રેક કરો',
    mr: 'आपली कारागिरी सुरक्षितपणे ट्रॅक करा'
  },
  'home.customers.title': {
    en: 'Customers',
    hi: 'ग्राहक',
    pa: 'ਗਾਹਕ',
    bn: 'গ্রাহক',
    te: 'కస్టమర్‌లు',
    ta: 'வாடிக்கையாளர்கள்',
    ml: 'ഉപഭോക്താക്കൾ',
    kn: 'ಗ್ರಾಹಕರು',
    gu: 'ગ્રાહકો',
    mr: 'ग्राहक'
  },
  'home.customers.description': {
    en: 'Master Ji makes it simple to stay connected with your tailor throughout the entire process. No hassle—just one free place to track and manage everything.',
    hi: 'मास्टर जी पूरी प्रक्रिया के दौरान अपने दर्जी के साथ जुड़े रहना आसान बनाता है। कोई परेशानी नहीं—सिर्फ सब कुछ ट्रैक और प्रबंधित करने के लिए एक मुफ्त स्थान।',
    pa: 'ਮਾਸਟਰ ਜੀ ਪੂਰੀ ਪ੍ਰਕਿਰਿਆ ਦੇ ਦੌਰਾਨ ਆਪਣੇ ਦਰਜ਼ੀ ਨਾਲ ਜੁੜੇ ਰਹਿਣਾ ਸੌਖਾ ਬਣਾਉਂਦਾ ਹੈ। ਕੋਈ ਤੰਗੀ ਨਹੀਂ—ਸਿਰਫ ਸਭ ਕੁਝ ਟ੍ਰੈਕ ਅਤੇ ਪ੍ਰਬੰਧਿਤ ਕਰਨ ਲਈ ਇੱਕ ਮੁਫਤ ਸਥਾਨ।',
    bn: 'মাস্টার জি পুরো প্রক্রিয়া জুড়ে আপনার দর্জির সাথে সংযুক্ত থাকা সহজ করে তোলে। কোন ঝামেলা নেই—শুধু সবকিছু ট্র্যাক এবং পরিচালনা করার জন্য একটি বিনামূল্যের স্থান।',
    te: 'మాస్టర్ జీ మొత్తం ప్రక్రియలో మీ దర్జీతో కనెక్ట్‌గా ఉండడాన్ని సులభతరం చేస్తుంది। ఎటువంటి ఇబ్బంది లేదు—ప్రతిదీ ట్రాక్ చేయడానికి మరియు నిర్వహించడానికి ఒక ఉచిత స్థలం।',
    ta: 'மாஸ்டர் ஜி முழு செயல்முறையிலும் உங்கள் தையல்காரருடன் இணைந்திருக்க எளிதாக்குகிறது। எந்த சிக்கலும் இல்லை—எல்லாவற்றையும் கண்காணிக்கவும் நிர்வகிக்கவும் ஒரு இலவச இடம் மட்டுமே।',
    ml: 'മാസ്റ്റർ ജി മുഴുവൻ പ്രക്രിയയിലുടനീളം നിങ്ങളുടെ ടെയ്ലറുമായി ബന്ധപ്പെട്ടിരിക്കുന്നത് എളുപ്പമാക്കുന്നു। ബുദ്ധിമുട്ട് ഇല്ല—എല്ലാം ട്രാക്ക് ചെയ്യാനും നിയന്ത്രിക്കാനും ഒരു സൗജന്യ സ്ഥലം മാത്രം।',
    kn: 'ಮಾಸ್ಟರ್ ಜಿ ಸಂಪೂರ್ಣ ಪ್ರಕ್ರಿಯೆಯಾದ್ಯಂತ ನಿಮ್ಮ ಟೈಲರ್‌ನೊಂದಿಗೆ ಸಂಪರ್ಕದಲ್ಲಿರಲು ಸುಲಭಗೊಳಿಸುತ್ತದೆ। ಯಾವುದೇ ತೊಂದರೆ ಇಲ್ಲ—ಎಲ್ಲವನ್ನೂ ಟ್ರ್ಯಾಕ್ ಮಾಡಲು ಮತ್ತು ನಿರ್ವಹಿಸಲು ಒಂದು ಉಚಿತ ಸ್ಥಳ ಮಾತ್ರ।',
    gu: 'માસ્ટર જી સંપૂર્ણ પ્રક્રિયા દરમિયાન તમારા ટેલર સાથે જોડાયેલા રહેવાનું સરળ બનાવે છે। કોઈ તકલીફ નથી—ફક્ત બધું ટ્રેક અને વ્યવસ્થાપિત કરવા માટે એક મફત સ્થાન।',
    mr: 'मास्टर जी संपूर्ण प्रक्रियेदरम्यान तुमच्या टेलरशी जोडलेले राहणे सोपे करते। कोणतीही त्रासदायक नाही—फक्त सर्वकाही ट्रॅक आणि व्यवस्थापित करण्यासाठी एक विनामूल्य ठिकाण।'
  },
  'home.customers.feature1.title': {
    en: 'Real-Time Tracking',
    hi: 'रीयल-टाइम ट्रैकिंग',
    pa: 'ਰੀਅਲ-ਟਾਈਮ ਟ੍ਰੈਕਿੰਗ',
    bn: 'রিয়েল-টাইম ট্র্যাকিং',
    te: 'రియల్-టైమ్ ట్రాకింగ్',
    ta: 'நேரடி கண்காணிப்பு',
    ml: 'റിയൽ-ടൈം ട്രാക്കിംഗ്',
    kn: 'ರಿಯಲ್-ಟೈಮ್ ಟ್ರ್ಯಾಕಿಂಗ್',
    gu: 'રિયલ-ટાઇમ ટ્રેકિંગ',
    mr: 'रिअल-टाइम ट्रॅकिंग'
  },
  'home.customers.feature1.description': {
    en: 'Watch the progress of your garments, from fabric selection to final stitch.',
    hi: 'कपड़े के चयन से लेकर अंतिम सिलाई तक अपने कपड़ों की प्रगति देखें।',
    pa: 'ਕੱਪੜੇ ਦੀ ਚੋਣ ਤੋਂ ਲੈ ਕੇ ਅੰਤਿਮ ਸਿਲਾਈ ਤੱਕ ਆਪਣੇ ਕੱਪੜਿਆਂ ਦੀ ਤਰੱਕੀ ਦੇਖੋ।',
    bn: 'কাপড় নির্বাচন থেকে শেষ সেলাই পর্যন্ত আপনার পোশাকের অগ্রগতি দেখুন।',
    te: 'ఫాబ్రిక్ ఎంపిక నుండి చివరి కుట్టు వరకు మీ వస్త్రాల పురోగతిని చూడండి।',
    ta: 'துணி தேர்விலிருந்து இறுதி தையல் வரை உங்கள் ஆடைகளின் முன்னேற்றத்தைப் பாருங்கள்।',
    ml: 'ഫാബ്രിക് തിരഞ്ഞെടുപ്പിൽ നിന്ന് അവസാന തുന്നൽ വരെ നിങ്ങളുടെ വസ്ത്രങ്ങളുടെ പുരോഗതി കാണുക।',
    kn: 'ಫ್ಯಾಬ್ರಿಕ್ ಆಯ್ಕೆಯಿಂದ ಅಂತಿಮ ಹೊಲಿಗೆ ವರೆಗೆ ನಿಮ್ಮ ವಸ್ತ್ರಗಳ ಪ್ರಗತಿಯನ್ನು ನೋಡಿ।',
    gu: 'ફેબ્રિક પસંદગીથી અંતિમ ટાંકા સુધી તમારા કપડાંની પ્રગતિ જુઓ।',
    mr: 'फॅब्रिक निवडीपासून अंतिम शिवणापर्यंत तुमच्या कपड्यांची प्रगती पहा।'
  },
  'home.customers.feature2.title': {
    en: 'Measurement Control',
    hi: 'माप नियंत्रण',
    pa: 'ਮਾਪ ਨਿਯੰਤਰਣ',
    bn: 'পরিমাপ নিয়ন্ত্রণ',
    te: 'కొలత నియంత్రణ',
    ta: 'அளவீடு கட்டுப்பாடு',
    ml: 'അളവ് നിയന്ത്രണം',
    kn: 'ಮಾಪನ ನಿಯಂತ್ರಣ',
    gu: 'માપ નિયંત્રણ',
    mr: 'मोजमाप नियंत्रण'
  },
  'home.customers.feature2.description': {
    en: 'Review, manage, and secure your personal measurement data for consistently accurate fittings.',
    hi: 'लगातार सटीक फिटिंग के लिए अपने व्यक्तिगत माप डेटा की समीक्षा, प्रबंधन और सुरक्षा करें।',
    pa: 'ਨਿਰੰਤਰ ਸਹੀ ਫਿਟਿੰਗ ਲਈ ਆਪਣੇ ਨਿੱਜੀ ਮਾਪ ਡੇਟਾ ਦੀ ਸਮੀਖਿਆ, ਪ੍ਰਬੰਧਨ ਅਤੇ ਸੁਰੱਖਿਆ ਕਰੋ।',
    bn: 'ধারাবাহিকভাবে সঠিক ফিটিংয়ের জন্য আপনার ব্যক্তিগত পরিমাপ ডেটা পর্যালোচনা, পরিচালনা এবং সুরক্ষিত করুন।',
    te: 'స్థిరంగా ఖచ్చితమైన ఫిటింగ్‌ల కోసం మీ వ్యక్తిగత కొలత డేటాను సమీక్షించండి, నిర్వహించండి మరియు సురక్షితం చేయండి।',
    ta: 'தொடர்ச்சியாக துல்லியமான பொருத்தங்களுக்கு உங்கள் தனிப்பட்ட அளவீட்டு தரவை மதிப்பாய்வு செய்யவும், நிர்வகிக்கவும் மற்றும் பாதுகாக்கவும்।',
    ml: 'സ്ഥിരമായി കൃത്യമായ ഫിറ്റിംഗുകൾക്കായി നിങ്ങളുടെ വ്യക്തിഗത അളവ് ഡാറ്റ അവലോകനം ചെയ്യുക, നിയന്ത്രിക്കുക, സുരക്ഷിതമാക്കുക।',
    kn: 'ಸ್ಥಿರವಾಗಿ ನಿಖರವಾದ ಫಿಟಿಂಗ್‌ಗಳಿಗಾಗಿ ನಿಮ್ಮ ವೈಯಕ್ತಿಕ ಮಾಪನ ಡೇಟಾವನ್ನು ವಿಮರ್ಶೆ ಮಾಡಿ, ನಿರ್ವಹಿಸಿ ಮತ್ತು ಸುರಕ್ಷಿತಗೊಳಿಸಿ।',
    gu: 'સતત સચોટ ફિટિંગ માટે તમારા વ્યક્તિગત માપ ડેટાની સમીક્ષા કરો, વ્યવસ્થાપિત કરો અને સુરક્ષિત કરો।',
    mr: 'सतत अचूक फिटिंगसाठी तुमच्या वैयक्तिक मोजमाप डेटाचे पुनरावलोकन करा, व्यवस्थापित करा आणि सुरक्षित करा।'
  },
  'home.customers.feature3.title': {
    en: 'Direct Updates',
    hi: 'प्रत्यक्ष अपडेट',
    pa: 'ਸਿੱਧੇ ਅਪਡੇਟ',
    bn: 'সরাসরি আপডেট',
    te: 'ప్రత్యక్ష నవీకరణలు',
    ta: 'நேரடி புதுப்பிப்புகள்',
    ml: 'നേരിട്ടുള്ള അപ്ഡേറ്റുകൾ',
    kn: 'ನೇರ ನವೀಕರಣಗಳು',
    gu: 'સીધા અપડેટ્સ',
    mr: 'थेट अपडेट्स'
  },
  'home.customers.feature3.description': {
    en: 'Receive production status updates directly from your tailor, building transparent trust.',
    hi: 'अपने दर्जी से सीधे उत्पादन स्थिति अपडेट प्राप्त करें, पारदर्शी विश्वास बनाएं।',
    pa: 'ਆਪਣੇ ਦਰਜ਼ੀ ਤੋਂ ਸਿੱਧੇ ਉਤਪਾਦਨ ਸਥਿਤੀ ਅਪਡੇਟ ਪ੍ਰਾਪਤ ਕਰੋ, ਪਾਰਦਰਸ਼ੀ ਭਰੋਸਾ ਬਣਾਓ।',
    bn: 'আপনার দর্জির কাছ থেকে সরাসরি উৎপাদন অবস্থা আপডেট পান, স্বচ্ছ বিশ্বাস গড়ে তুলুন।',
    te: 'మీ దర్జీ నుండి నేరుగా ఉత్పాదన స్థితి నవీకరణలను స్వీకరించండి, పారదర్శకమైన విశ్వాసాన్ని నిర్మించండి।',
    ta: 'உங்கள் தையல்காரரிடமிருந்து நேரடியாக உற்பத்தி நிலை புதுப்பிப்புகளைப் பெறுங்கள், வெளிப்படையான நம்பிக்கையை உருவாக்குங்கள்।',
    ml: 'നിങ്ങളുടെ ടെയ്ലറിൽ നിന്ന് നേരിട്ട് ഉൽപാദന സ്ഥിതി അപ്ഡേറ്റുകൾ സ്വീകരിക്കുക, സുതാര്യമായ വിശ്വാസം നിർമ്മിക്കുക।',
    kn: 'ನಿಮ್ಮ ಟೈಲರ್‌ನಿಂದ ನೇರವಾಗಿ ಉತ್ಪಾದನಾ ಸ್ಥಿತಿ ನವೀಕರಣಗಳನ್ನು ಸ್ವೀಕರಿಸಿ, ಪಾರದರ್ಶಕ ವಿಶ್ವಾಸವನ್ನು ನಿರ್ಮಿಸಿ।',
    gu: 'તમારા ટેલર પાસેથી સીધા ઉત્પાદન સ્થિતિ અપડેટ્સ પ્રાપ્ત કરો, પારદર્શક વિશ્વાસ બનાવો।',
    mr: 'तुमच्या टेलरकडून थेट उत्पादन स्थिती अपडेट्स मिळवा, पारदर्शक विश्वास तयार करा।'
  },

  // Homepage Businesses Section
  'home.businesses.badge': {
    en: 'Focus on tools, efficiency, and growth.',
    hi: 'उपकरण, दक्षता और विकास पर ध्यान दें।',
    pa: 'ਟੂਲਾਂ, ਕੁਸ਼ਲਤਾ ਅਤੇ ਵਿਕਾਸ \'ਤੇ ਧਿਆਨ ਦਿਓ।',
    bn: 'সরঞ্জাম, দক্ষতা এবং বৃদ্ধির উপর ফোকাস করুন।',
    te: 'సాధనాలు, సామర్థ్యం మరియు వృద్ధిపై దృష్టి పెట్టండి।',
    ta: 'கருவிகள், திறன் மற்றும் வளர்ச்சியில் கவனம் செலுத்துங்கள்।',
    ml: 'ഉപകരണങ്ങൾ, കാര്യക്ഷമത, വളർച്ച എന്നിവയിൽ ശ്രദ്ധ കേന്ദ്രീകരിക്കുക।',
    kn: 'ಉಪಕರಣಗಳು, ದಕ್ಷತೆ ಮತ್ತು ಬೆಳವಣಿಗೆಯ ಮೇಲೆ ಗಮನ ಹರಿಸಿ।',
    gu: 'સાધનો, કાર્યક્ષમતા અને વૃદ્ધિ પર ધ્યાન કેન્દ્રિત કરો।',
    mr: 'साधने, कार्यक्षमता आणि वाढीवर लक्ष केंद्रित करा।'
  },
  'home.businesses.title': {
    en: 'Businesses',
    hi: 'व्यवसाय',
    pa: 'ਵਪਾਰ',
    bn: 'ব্যবসা',
    te: 'వ్యాపారాలు',
    ta: 'வணிகங்கள்',
    ml: 'ബിസിനസുകൾ',
    kn: 'ವ್ಯವಹಾರಗಳು',
    gu: 'વ્યવસાયો',
    mr: 'व्यवसाय'
  },
  'home.businesses.description': {
    en: 'Whether you are a tailor or a designer, Master Ji gives you powerful, simple tools to focus on exceptional clothing while we handle the management.',
    hi: 'चाहे आप दर्जी हों या डिजाइनर, मास्टर जी आपको शक्तिशाली, सरल उपकरण देता है ताकि आप असाधारण कपड़ों पर ध्यान केंद्रित कर सकें जबकि हम प्रबंधन संभालते हैं।',
    pa: 'ਚਾਹੇ ਤੁਸੀਂ ਦਰਜ਼ੀ ਹੋ ਜਾਂ ਡਿਜ਼ਾਈਨਰ, ਮਾਸਟਰ ਜੀ ਤੁਹਾਨੂੰ ਸ਼ਕਤੀਸ਼ਾਲੀ, ਸਰਲ ਟੂਲ ਦਿੰਦਾ ਹੈ ਤਾਂ ਜੋ ਤੁਸੀਂ ਸ਼ਾਨਦਾਰ ਕੱਪੜਿਆਂ \'ਤੇ ਧਿਆਨ ਕੇਂਦਰਿਤ ਕਰ ਸਕੋ ਜਦੋਂ ਕਿ ਅਸੀਂ ਪ੍ਰਬੰਧਨ ਸੰਭਾਲਦੇ ਹਾਂ।',
    bn: 'আপনি একজন দর্জি বা ডিজাইনার হোন না কেন, মাস্টার জি আপনাকে শক্তিশালী, সহজ সরঞ্জাম দেয় যাতে আপনি অসাধারণ পোশাকের উপর ফোকাস করতে পারেন যখন আমরা ব্যবস্থাপনা পরিচালনা করি।',
    te: 'మీరు దర్జీ అయినా లేదా డిజైనర్ అయినా, మాస్టర్ జీ మీకు శక్తివంతమైన, సరళమైన సాధనాలను అందిస్తుంది, తద్వారా మేము నిర్వహణను నిర్వహించేటప్పుడు మీరు అనూహ్యమైన వస్త్రాలపై దృష్టి పెట్టవచ్చు।',
    ta: 'நீங்கள் தையல்காரராக இருந்தாலும் அல்லது வடிவமைப்பாளராக இருந்தாலும், மாஸ்டர் ஜி உங்களுக்கு சக்திவாய்ந்த, எளிய கருவிகளை வழங்குகிறது, இதனால் நாங்கள் மேலாண்மையை கையாளும்போது நீங்கள் விதிவிலக்கான ஆடைகளில் கவனம் செலுத்தலாம்।',
    ml: 'നിങ്ങൾ ടെയ്ലറാണോ ഡിസൈനറാണോ, മാസ്റ്റർ ജി നിങ്ങൾക്ക് ശക്തമായ, ലളിതമായ ഉപകരണങ്ങൾ നൽകുന്നു, അതിനാൽ ഞങ്ങൾ മാനേജ്‌മെന്റ് കൈകാര്യം ചെയ്യുമ്പോൾ നിങ്ങൾക്ക് അസാധാരണമായ വസ്ത്രങ്ങളിൽ ശ്രദ്ധ കേന്ദ്രീകരിക്കാം।',
    kn: 'ನೀವು ಟೈಲರ್ ಆಗಿರಲಿ ಅಥವಾ ಡಿಸೈನರ್ ಆಗಿರಲಿ, ಮಾಸ್ಟರ್ ಜಿ ನಿಮಗೆ ಶಕ್ತಿಶಾಲಿ, ಸರಳವಾದ ಉಪಕರಣಗಳನ್ನು ನೀಡುತ್ತದೆ, ಆದ್ದರಿಂದ ನಾವು ನಿರ್ವಹಣೆಯನ್ನು ನಿರ್ವಹಿಸುವಾಗ ನೀವು ಅಸಾಧಾರಣವಾದ ವಸ್ತ್ರಗಳ ಮೇಲೆ ಗಮನ ಹರಿಸಬಹುದು।',
    gu: 'તમે ટેલર છો કે ડિઝાઇનર છો, માસ્ટર જી તમને શક્તિશાળી, સરળ સાધનો આપે છે જેથી તમે અસાધારણ કપડાં પર ધ્યાન કેન્દ્રિત કરી શકો જ્યારે અમે વ્યવસ્થાપન સંભાળીએ છીએ।',
    mr: 'तुम्ही टेलर असाल किंवा डिझायनर असाल, मास्टर जी तुम्हाला शक्तिशाली, सोपी साधने देते जेणेकरून तुम्ही असाधारण कपड्यांवर लक्ष केंद्रित करू शकता तर आम्ही व्यवस्थापन हाताळतो।'
  },
  'home.businesses.feature1.title': {
    en: 'All-in-One Management',
    hi: 'ऑल-इन-वन प्रबंधन',
    pa: 'ਆਲ-ਇਨ-ਵਨ ਪ੍ਰਬੰਧਨ',
    bn: 'অল-ইন-ওয়ান ম্যানেজমেন্ট',
    te: 'అన్నీ-ఒక-నిర్వహణ',
    ta: 'அனைத்தும்-ஒன்று-நிர்வாகம்',
    ml: 'ഓൾ-ഇൻ-വൺ മാനേജ്‌മെന്റ്',
    kn: 'ಎಲ್ಲಾ-ಒಂದು-ನಿರ್ವಹಣೆ',
    gu: 'ઓલ-ઇન-વન મેનેજમેન્ટ',
    mr: 'ऑल-इन-वन व्यवस्थापन'
  },
  'home.businesses.feature1.description': {
    en: 'Centralize customers, measurements, patterns, and orders in one secure CRM.',
    hi: 'एक सुरक्षित CRM में ग्राहकों, मापों, पैटर्न और ऑर्डर को केंद्रीकृत करें।',
    pa: 'ਇੱਕ ਸੁਰੱਖਿਤ CRM ਵਿੱਚ ਗਾਹਕਾਂ, ਮਾਪਾਂ, ਪੈਟਰਨ ਅਤੇ ਆਰਡਰਾਂ ਨੂੰ ਕੇਂਦਰੀਕ੍ਰਿਤ ਕਰੋ।',
    bn: 'একটি নিরাপদ CRM-এ গ্রাহক, পরিমাপ, প্যাটার্ন এবং অর্ডার কেন্দ্রীভূত করুন।',
    te: 'ఒక సురక్షితమైన CRM లో కస్టమర్‌లు, కొలతలు, నమూనాలు మరియు ఆర్డర్‌లను కేంద్రీకృతం చేయండి।',
    ta: 'ஒரு பாதுகாப்பான CRM இல் வாடிக்கையாளர்கள், அளவீடுகள், வடிவங்கள் மற்றும் ஆர்டர்களை மையப்படுத்துங்கள்।',
    ml: 'ഒരു സുരക്ഷിതമായ CRM-ൽ ഉപഭോക്താക്കൾ, അളവുകൾ, പാറ്റേണുകൾ, ഓർഡറുകൾ കേന്ദ്രീകരിക്കുക।',
    kn: 'ಒಂದು ಸುರಕ್ಷಿತ CRM ನಲ್ಲಿ ಗ್ರಾಹಕರು, ಮಾಪನಗಳು, ಮಾದರಿಗಳು ಮತ್ತು ಆರ್ಡರ್‌ಗಳನ್ನು ಕೇಂದ್ರೀಕೃತಗೊಳಿಸಿ।',
    gu: 'એક સુરક્ષિત CRM માં ગ્રાહકો, માપ, પેટર્ન અને ઓર્ડર કેન્દ્રિત કરો।',
    mr: 'एक सुरक्षित CRM मध्ये ग्राहक, मोजमाप, नमुने आणि ऑर्डर केंद्रित करा।'
  },
  'home.businesses.feature2.title': {
    en: 'Data-Driven Insights',
    hi: 'डेटा-संचालित अंतर्दृष्टि',
    pa: 'ਡੇਟਾ-ਆਧਾਰਿਤ ਸੂਝਾਂ',
    bn: 'ডেটা-চালিত অন্তর্দৃষ্টি',
    te: 'డేటా-ఆధారిత అంతర్దృష్టులు',
    ta: 'தரவு-ஆதாரமான நுண்ணறிவுகள்',
    ml: 'ഡാറ്റ-ചാലിത ഉൾക്കാഴ്ചകൾ',
    kn: 'ಡೇಟಾ-ಚಾಲಿತ ಒಳನೋಟಗಳು',
    gu: 'ડેટા-ચાલિત સૂઝ',
    mr: 'डेटा-चालित अंतर्दृष्टी'
  },
  'home.businesses.feature2.description': {
    en: 'Get reports to understand performance, streamline production times, and make smarter business decisions.',
    hi: 'प्रदर्शन को समझने, उत्पादन समय को सुव्यवस्थित करने और स्मार्ट व्यावसायिक निर्णय लेने के लिए रिपोर्ट प्राप्त करें।',
    pa: 'ਪ੍ਰਦਰਸ਼ਨ ਨੂੰ ਸਮਝਣ, ਉਤਪਾਦਨ ਸਮੇਂ ਨੂੰ ਸੁਵਿਵਸਥਿਤ ਕਰਨ ਅਤੇ ਸਮਾਰਟ ਵਪਾਰੀ ਫੈਸਲੇ ਲੈਣ ਲਈ ਰਿਪੋਰਟਾਂ ਪ੍ਰਾਪਤ ਕਰੋ।',
    bn: 'কর্মক্ষমতা বোঝার জন্য, উৎপাদন সময়কে সুবিন্যস্ত করার জন্য এবং স্মার্ট ব্যবসায়িক সিদ্ধান্ত নেওয়ার জন্য রিপোর্ট পান।',
    te: 'పనితీరును అర్థం చేసుకోవడానికి, ఉత్పాదన సమయాలను సులభతరం చేయడానికి మరియు తెలివైన వ్యాపార నిర్ణయాలు తీసుకోవడానికి నివేదికలను పొందండి।',
    ta: 'செயல்திறனைப் புரிந்துகொள்ள, உற்பத்தி நேரங்களை திறம்பட மாற்ற, மற்றும் புத்திசாலித்தனமான வணிக முடிவுகளை எடுக்க அறிக்கைகளைப் பெறுங்கள்।',
    ml: 'പ്രകടനം മനസ്സിലാക്കാനും, ഉൽപാദന സമയങ്ങൾ ക്രമീകരിക്കാനും, സ്മാർട്ട് ബിസിനസ് തീരുമാനങ്ങൾ എടുക്കാനും റിപ്പോർട്ടുകൾ നേടുക।',
    kn: 'ಪ್ರದರ್ಶನವನ್ನು ಅರ್ಥಮಾಡಿಕೊಳ್ಳಲು, ಉತ್ಪಾದನಾ ಸಮಯಗಳನ್ನು ಸುಗಮಗೊಳಿಸಲು ಮತ್ತು ಸ್ಮಾರ್ಟ್ ವ್ಯಾಪಾರ ನಿರ್ಧಾರಗಳನ್ನು ತೆಗೆದುಕೊಳ್ಳಲು ವರದಿಗಳನ್ನು ಪಡೆಯಿರಿ।',
    gu: 'પ્રદર્શન સમજવા, ઉત્પાદન સમયને સુવ્યવસ્થિત કરવા અને સ્માર્ટ વ્યવસાયિક નિર્ણયો લેવા માટે અહેવાલો મેળવો।',
    mr: 'कामगिरी समजून घेण्यासाठी, उत्पादन वेळ सुलभ करण्यासाठी आणि स्मार्ट व्यावसायिक निर्णय घेण्यासाठी अहवाल मिळवा।'
  },
  'home.businesses.feature3.title': {
    en: 'Customer Loyalty',
    hi: 'ग्राहक वफादारी',
    pa: 'ਗਾਹਕ ਵਫ਼ਾਦਾਰੀ',
    bn: 'গ্রাহক আনুগত্য',
    te: 'కస్టమర్ విశ్వాసం',
    ta: 'வாடிக்கையாளர் விசுவாசம்',
    ml: 'ഉപഭോക്തൃ വിശ്വസ്തത',
    kn: 'ಗ್ರಾಹಕ ನಿಷ್ಠೆ',
    gu: 'ગ્રાહક વફાદારી',
    mr: 'ग्राहक निष्ठा'
  },
  'home.businesses.feature3.description': {
    en: 'Automate communication and transparency, building lasting relationships and repeat business.',
    hi: 'संचार और पारदर्शिता को स्वचालित करें, स्थायी संबंध और दोहराव वाले व्यवसाय का निर्माण करें।',
    pa: 'ਸੰਚਾਰ ਅਤੇ ਪਾਰਦਰਸ਼ਤਾ ਨੂੰ ਸਵੈਚਾਲਿਤ ਕਰੋ, ਸਥਾਈ ਸੰਬੰਧ ਅਤੇ ਦੁਹਰਾਉਣ ਵਾਲੇ ਵਪਾਰ ਦਾ ਨਿਰਮਾਣ ਕਰੋ।',
    bn: 'যোগাযোগ এবং স্বচ্ছতাকে স্বয়ংক্রিয় করুন, স্থায়ী সম্পর্ক এবং পুনরাবৃত্ত ব্যবসা গড়ে তুলুন।',
    te: 'కమ్యూనికేషన్ మరియు పారదర్శకతను ఆటోమేట్ చేయండి, శాశ్వత సంబంధాలు మరియు పునరావృత వ్యాపారాన్ని నిర్మించండి।',
    ta: 'தொடர்பு மற்றும் வெளிப்படைத்தன்மையை தானியங்கி செய்யுங்கள், நீடித்த உறவுகள் மற்றும் மீண்டும் மீண்டும் வணிகத்தை உருவாக்குங்கள்।',
    ml: 'ആശയവിനിമയവും സുതാര്യതയും ഓട്ടോമേറ്റ് ചെയ്യുക, നീണ്ടുനിൽക്കുന്ന ബന്ധങ്ങളും ആവർത്തിച്ചുള്ള ബിസിനസും നിർമ്മിക്കുക।',
    kn: 'ಸಂವಹನ ಮತ್ತು ಪಾರದರ್ಶಕತೆಯನ್ನು ಸ್ವಯಂಚಾಲಿತಗೊಳಿಸಿ, ಶಾಶ್ವತ ಸಂಬಂಧಗಳು ಮತ್ತು ಪುನರಾವರ್ತಿತ ವ್ಯಾಪಾರವನ್ನು ನಿರ್ಮಿಸಿ।',
    gu: 'કમ્યુનિકેશન અને પારદર્શિતાને ઓટોમેટ કરો, ટકાઉ સંબંધો અને પુનરાવર્તિત વ્યવસાય બનાવો।',
    mr: 'संप्रेषण आणि पारदर्शकता स्वयंचलित करा, टिकाऊ नातेसंबंध आणि पुनरावृत्ती व्यवसाय तयार करा।'
  },

  // How It Works Hero Section
  'how_it_works.hero.title': {
    en: 'Your Style, Perfected Digitally',
    hi: 'आपकी शैली, डिजिटल रूप से परिपूर्ण',
    pa: 'ਤੁਹਾਡੀ ਸ਼ੈਲੀ, ਡਿਜੀਟਲ ਰੂਪ ਵਿੱਚ ਸੰਪੂਰਨ',
    bn: 'আপনার স্টাইল, ডিজিটালভাবে নিখুঁত',
    te: 'మీ స్టైల్, డిజిటల్‌గా పరిపూర్ణం',
    ta: 'உங்கள் பாணி, டிஜிட்டலாக சரியானது',
    ml: 'നിങ്ങളുടെ ശൈലി, ഡിജിറ്റലായി പൂർണ്ണമാക്കിയത്',
    kn: 'ನಿಮ್ಮ ಶೈಲಿ, ಡಿಜಿಟಲ್‌ಗಾಗಿ ಪರಿಪೂರ್ಣ',
    gu: 'તમારી શૈલી, ડિજિટલ રીતે પરિપૂર્ણ',
    mr: 'तुमची शैली, डिजिटल पद्धतीने परिपूर्ण'
  },
  'how_it_works.hero.description': {
    en: 'Experience the future of custom tailoring with Master Ji. Our platform connects customers to skilled tailors and empowers businesses to manage their craft with unparalleled efficiency.',
    hi: 'मास्टर जी के साथ कस्टम टेलरिंग के भविष्य का अनुभव करें। हमारा प्लेटफॉर्म ग्राहकों को कुशल दर्जी से जोड़ता है और व्यवसायों को अतुलनीय दक्षता के साथ अपने शिल्प का प्रबंधन करने में सक्षम बनाता है।',
    pa: 'ਮਾਸਟਰ ਜੀ ਨਾਲ ਕਸਟਮ ਟੇਲਰਿੰਗ ਦੇ ਭਵਿੱਖ ਦਾ ਅਨੁਭਵ ਕਰੋ। ਸਾਡਾ ਪਲੇਟਫਾਰਮ ਗਾਹਕਾਂ ਨੂੰ ਕੁਸ਼ਲ ਦਰਜ਼ੀਆਂ ਨਾਲ ਜੋੜਦਾ ਹੈ ਅਤੇ ਵਪਾਰਾਂ ਨੂੰ ਬੇਮਿਸਾਲ ਕੁਸ਼ਲਤਾ ਨਾਲ ਆਪਣੇ ਕਾਰੀਗਰੀ ਦਾ ਪ੍ਰਬੰਧਨ ਕਰਨ ਦੀ ਸਮਰੱਥਾ ਦਿੰਦਾ ਹੈ।',
    bn: 'মাস্টার জির সাথে কাস্টম টেইলরিংয়ের ভবিষ্যতের অভিজ্ঞতা নিন। আমাদের প্ল্যাটফর্ম গ্রাহকদের দক্ষ দর্জির সাথে সংযুক্ত করে এবং ব্যবসাগুলিকে অতুলনীয় দক্ষতার সাথে তাদের কারুশিল্প পরিচালনা করতে সক্ষম করে।',
    te: 'మాస్టర్ జీతో కస్టమ్ టైలరింగ్ యొక్క భవిష్యత్తును అనుభవించండి। మా ప్లాట్‌ఫారమ్ కస్టమర్‌లను నైపుణ్యం కలిగిన టైలర్‌లతో కనెక్ట్ చేస్తుంది మరియు వ్యాపారాలను అసమానమైన సామర్థ్యంతో వారి కరకౌశలాన్ని నిర్వహించడానికి అనుమతిస్తుంది।',
    ta: 'மாஸ்டர் ஜியுடன் தனிப்பயன் தையலின் எதிர்காலத்தை அனுபவிக்கவும்। எங்கள் தளம் வாடிக்கையாளர்களை திறமையான தையல்காரர்களுடன் இணைக்கிறது மற்றும் வணிகங்களை ஒப்பிட முடியாத திறனுடன் தங்கள் கைவினைத்திறனை நிர்வகிக்க அனுமதிக்கிறது।',
    ml: 'മാസ്റ്റർ ജിയുമായി കസ്റ്റം ടെയ്ലറിംഗിന്റെ ഭാവിയെ അനുഭവിക്കുക। ഞങ്ങളുടെ പ്ലാറ്റ്‌ഫോം ഉപഭോക്താക്കളെ കഴിവുള്ള ടെയ്ലറുകളുമായി ബന്ധിപ്പിക്കുന്നു, ബിസിനസുകൾക്ക് അസാധാരണമായ കാര്യക്ഷമതയോടെ അവരുടെ കരകൗശലം നിയന്ത്രിക്കാൻ അനുവദിക്കുന്നു।',
    kn: 'ಮಾಸ್ಟರ್ ಜಿಯೊಂದಿಗೆ ಕಸ್ಟಮ್ ಟೈಲರಿಂಗ್‌ನ ಭವಿಷ್ಯವನ್ನು ಅನುಭವಿಸಿ। ನಮ್ಮ ಪ್ಲಾಟ್‌ಫಾರ್ಮ್ ಗ್ರಾಹಕರನ್ನು ನೈಪುಣ್ಯತೆಯ ಟೈಲರ್‌ಗಳೊಂದಿಗೆ ಸಂಪರ್ಕಿಸುತ್ತದೆ ಮತ್ತು ವ್ಯವಹಾರಗಳಿಗೆ ಅಸಮಾನವಾದ ದಕ್ಷತೆಯೊಂದಿಗೆ ಅವರ ಕರಕೌಶಲವನ್ನು ನಿರ್ವಹಿಸಲು ಅನುಮತಿಸುತ್ತದೆ।',
    gu: 'માસ્ટર જી સાથે કસ્ટમ ટેલરિંગના ભવિષ્યનો અનુભવ લો। અમારું પ્લેટફોર્મ ગ્રાહકોને કુશળ ટેલરો સાથે જોડે છે અને વ્યવસાયોને અસાધારણ કાર્યક્ષમતા સાથે તેમના કારીગરીનું વ્યવસ્થાપન કરવા માટે સક્ષમ બનાવે છે।',
    mr: 'मास्टर जीसोबत कस्टम टेलरिंगच्या भविष्याचा अनुभव घ्या। आमचे प्लॅटफॉर्म ग्राहकांना कुशल टेलरशी जोडते आणि व्यवसायांना अतुलनीय कार्यक्षमतेसह त्यांच्या कारागिरीचे व्यवस्थापन करण्यास सक्षम करते।'
  },
  'how_it_works.hero.cta': {
    en: 'Get Started Now',
    hi: 'अभी शुरू करें',
    pa: 'ਹੁਣੇ ਸ਼ੁਰੂ ਕਰੋ',
    bn: 'এখনই শুরু করুন',
    te: 'ఇప్పుడే ప్రారంభించండి',
    ta: 'இப்போது தொடங்கவும்',
    ml: 'ഇപ്പോൾ ആരംഭിക്കുക',
    kn: 'ಈಗ ಪ್ರಾರಂಭಿಸಿ',
    gu: 'હવે શરૂ કરો',
    mr: 'आत्ता सुरू करा'
  },

  // Contact Hero Section
  'contact.hero.title': {
    en: 'Drop Us a Line',
    hi: 'हमें एक पंक्ति लिखें',
    pa: 'ਸਾਨੂੰ ਇੱਕ ਲਾਈਨ ਲਿਖੋ',
    bn: 'আমাদের একটি লাইন লিখুন',
    te: 'మాకు ఒక లైన్ రాయండి',
    ta: 'எங்களுக்கு ஒரு வரி எழுதுங்கள்',
    ml: 'ഞങ്ങൾക്ക് ഒരു വരി എഴുതുക',
    kn: 'ನಮಗೆ ಒಂದು ಸಾಲು ಬರೆಯಿರಿ',
    gu: 'અમને એક લાઇન લખો',
    mr: 'आम्हाला एक ओळ लिहा'
  },
  'contact.hero.location': {
    en: 'New York, USA',
    hi: 'न्यूयॉर्क, यूएसए',
    pa: 'ਨਿਊਯਾਰਕ, ਯੂਐਸਏ',
    bn: 'নিউইয়র্ক, মার্কিন যুক্তরাষ্ট্র',
    te: 'న్యూయార్క్, USA',
    ta: 'நியூயார்க், USA',
    ml: 'ന്യൂയോർക്ക്, USA',
    kn: 'ನ್ಯೂಯಾರ್ಕ್, USA',
    gu: 'ન્યૂયોર્ક, USA',
    mr: 'न्यूयॉर्क, USA'
  },
  'contact.hero.email': {
    en: 'infotherealmasterji@gmail.com'
  },
  'contact.hero.phone': {
    en: '+1-9293448376'
  },

  // FAQs Hero Section
  'faqs.hero.title': {
    en: 'How can we help?',
    hi: 'हम कैसे मदद कर सकते हैं?',
    pa: 'ਅਸੀਂ ਕਿਵੇਂ ਮਦਦ ਕਰ ਸਕਦੇ ਹਾਂ?',
    bn: 'আমরা কিভাবে সাহায্য করতে পারি?',
    te: 'మేము ఎలా సహాయం చేయగలము?',
    ta: 'நாங்கள் எவ்வாறு உதவ முடியும்?',
    ml: 'ഞങ്ങൾക്ക് എങ്ങനെ സഹായിക്കാൻ കഴിയും?',
    kn: 'ನಾವು ಹೇಗೆ ಸಹಾಯ ಮಾಡಬಹುದು?',
    gu: 'અમે કેવી રીતે મદદ કરી શકીએ?',
    mr: 'आम्ही कसे मदत करू शकतो?'
  },
  'faqs.hero.search.placeholder': {
    en: 'Type your keyword',
    hi: 'अपना कीवर्ड टाइप करें',
    pa: 'ਆਪਣਾ ਕੀਵਰਡ ਟਾਈਪ ਕਰੋ',
    bn: 'আপনার কীওয়ার্ড টাইপ করুন',
    te: 'మీ కీవర్డ్ టైప్ చేయండి',
    ta: 'உங்கள் முக்கிய வார்த்தையை தட்டச்சு செய்யவும்',
    ml: 'നിങ്ങളുടെ കീവേഡ് ടൈപ്പ് ചെയ്യുക',
    kn: 'ನಿಮ್ಮ ಕೀವರ್ಡ್ ಟೈಪ್ ಮಾಡಿ',
    gu: 'તમારો કીવર્ડ ટાઇપ કરો',
    mr: 'तुमचा कीवर्ड टाइप करा'
  },

  // About Page
  'about.hero.title': { en: 'The Art of Tailoring, Reimagined.' },
  'about.hero.description': { en: 'Born from a passion for timeless craftsmanship and a vision for modern efficiency, Master Ji is the brainchild of Aman and Gurpreet. They saw a world where the intricate art of tailoring could thrive, seamlessly integrated with cutting-edge technology. This is their story, a fusion of tradition and innovation.' },
  'about.hero.alt': { en: 'Sewing machine and tailoring craftsmanship' },
  'about.problem.title': { en: 'The Unstitched Gap: Why Tailors Needed a Revolution' },
  'about.problem.description': { en: 'In an era of rapid digital transformation, the artisanal world of tailoring often found itself lagging. Manual processes, fragmented customer data, and limited digital presence hindered growth and efficiency. Our founders identified this critical market gap: a need for a specialized solution that understands the nuances of bespoke craftsmanship.' },
  'about.problem.1.title': { en: 'Manual Processes' },
  'about.problem.1.description': { en: 'Traditional tailoring relies heavily on manual record-keeping, leading to inefficiencies and human error in order management and customer data.' },
  'about.problem.2.title': { en: 'Limited Reach' },
  'about.problem.2.description': { en: 'Many talented tailors struggle to expand their customer base beyond local walk-ins, missing out on opportunities in the digital marketplace.' },
  'about.problem.3.title': { en: 'Lack of Digital Tools' },
  'about.problem.3.description': { en: 'The absence of specialized software means tailors often lack tools for efficient scheduling, digital measurements, and client communication.' },
  'about.mission.title': { en: "Our Mission: Efficiency for the Craft Community" },
  'about.mission.content.p1': { en: "Master Ji's mission is simple yet profound: to empower tailors by modernizing their workflows without compromising the soul of their craft. We provide intuitive tools that enable artisans to focus more on their creative work and less on tedious administrative tasks." },
  'about.mission.content.p2': { en: 'Our platform streamlines operations by automating appointment scheduling, digitizing measurement capture, and centralizing client communication. This reduces manual effort, minimizes errors, and ensures a seamless workflow from initial consultation to final delivery.' },
  'about.mission.content.p3': { en: 'In an increasingly digital world, Master Ji bridges the gap between traditional craftsmanship and modern technology. We help tailors expand their reach, manage their online presence, and leverage data insights to make informed business decisions—bringing their exquisite work to a wider, global audience.' },
  'about.mission.alt': { en: 'Woman with tablet in tailoring workshop' },
  'about.quote.text': { en: '"Our mission is simple: To empower tailors. By removing administrative friction, we allow craftsmen to focus on their art, not on their paperwork."' },
  'about.quote.author': { en: '— Gurpreet Singh Nanda, Co-Founder & CTO' },
  'about.features.title': { en: 'TailorTech Solutions: Features That Streamline Your Business' },
  'about.features.1.title': { en: 'Order & Project Management' },
  'about.features.1.description': { en: 'Track every order from initial consultation to final delivery, ensuring no detail is missed and deadlines are met efficiently.' },
  'about.features.2.title': { en: 'Client Scheduling & Appointments' },
  'about.features.2.description': { en: 'Simplify appointment booking and management, allowing clients to easily schedule fittings and consultations online.' },
  'about.features.3.title': { en: 'Invoicing & Payments' },
  'about.features.3.description': { en: 'Generate professional invoices, manage payments, and keep a clear record of all financial transactions with ease.' },
  'about.features.4.title': { en: 'Automated Communication' },
  'about.features.4.description': { en: 'Keep clients informed with automated updates, reminders, and personalized messages throughout the tailoring process.' },
  'about.features.5.title': { en: 'Performance Analytics' },
  'about.features.5.description': { en: 'Gain insights into business performance with intuitive dashboards showcasing sales trends, client retention, and workflow efficiency.' },
  'about.features.6.title': { en: 'Customization & Preferences' },
  'about.features.6.description': { en: 'Tailor the platform to your unique business needs, setting preferences for measurements, fabrics, and service offerings.' },
  'about.founders.title': { en: 'Our Visionaries: The Minds Behind Master Ji' },
  'about.founders.1.name': { en: 'Amandeep Singh Nanda' },
  'about.founders.1.role': { en: 'Co-Founder & CMO' },
  'about.founders.1.quote': { en: 'Our vision is to honor the timeless craft of tailoring by equipping artisans with the tools of tomorrow.' },
  'about.founders.2.name': { en: 'Gurpreet Singh Nanda' },
  'about.founders.2.role': { en: 'Co-Founder & CTO' },
  'about.founders.2.quote': { en: 'Technology should be an enabler, not a replacement for human skill. We build bridges between the two.' },
  'about.founders.3.name': { en: 'Simranjeet Kaur Narula' },
  'about.founders.3.role': { en: 'Co-Founder & CEO' },
  'about.founders.3.quote': { en: 'Every garment tells a story; our platform ensures that story is beautifully managed, from start to finish.' },
  'about.cta.title': { en: 'Ready to Transform Your Tailoring Business?' },
  'about.cta.description': { en: 'Join the growing community of tailors who are embracing the future with Master Ji. Experience the perfect blend of tradition and technology.' },
  'about.cta.button': { en: 'Get Started Today' },

  // Footer
  'footer.brand.tagline': {
    en: 'Stitch. Manage. Grow.',
    hi: 'सिलाई। प्रबंधन। विकास।',
    pa: 'ਸਿਲਾਈ। ਪ੍ਰਬੰਧਨ। ਵਿਕਾਸ।',
    bn: 'সেলাই। পরিচালনা। বৃদ্ধি।',
    te: 'కుట్టు। నిర్వహణ। పెంపు।',
    ta: 'தைப்பு। நிர்வாகம்। வளர்ச்சி।',
    ml: 'തുന്നൽ। നിയന്ത്രണം। വളർച്ച।',
    kn: 'ಹೊಲಿಗೆ। ನಿರ್ವಹಣೆ। ಬೆಳವಣಿಗೆ।',
    gu: 'સીવણ। વ્યવસ્થાપન। વૃદ્ધિ।',
    mr: 'शिवण। व्यवस्थापन। वाढ।'
  },
  'footer.company.title': {
    en: 'Company',
    hi: 'कंपनी',
    pa: 'ਕੰਪਨੀ',
    bn: 'কোম্পানি',
    te: 'కంపెనీ',
    ta: 'நிறுவனம்',
    ml: 'കമ്പനി',
    kn: 'ಕಂಪನಿ',
    gu: 'કંપની',
    mr: 'कंपनी'
  },
  'footer.company.about_us': {
    en: 'About Us',
    hi: 'हमारे बारे में',
    pa: 'ਸਾਡੇ ਬਾਰੇ',
    bn: 'আমাদের সম্পর্কে',
    te: 'మా గురించి',
    ta: 'எங்களைப் பற்றி',
    ml: 'ഞങ്ങളെക്കുറിച്ച്',
    kn: 'ನಮ್ಮ ಬಗ್ಗೆ',
    gu: 'અમારા વિશે',
    mr: 'आमच्याबद्दल'
  },
  'footer.company.faqs': {
    en: 'FAQs',
    hi: 'अक्सर पूछे जाने वाले प्रश्न',
    pa: 'ਅਕਸਰ ਪੁੱਛੇ ਜਾਣ ਵਾਲੇ ਸਵਾਲ',
    bn: 'প্রায়শই জিজ্ঞাসিত প্রশ্ন',
    te: 'తరచుగా అడిగే ప్రశ్నలు',
    ta: 'அடிக்கடி கேட்கப்படும் கேள்விகள்',
    ml: 'പതിവായി ചോദിക്കുന്ന ചോദ്യങ്ങൾ',
    kn: 'ಸಾಮಾನ್ಯವಾಗಿ ಕೇಳಲಾಗುವ ಪ್ರಶ್ನೆಗಳು',
    gu: 'વારંવાર પૂછાતા પ્રશ્નો',
    mr: 'वारंवार विचारले जाणारे प्रश्न'
  },
  'footer.company.contact': {
    en: 'Contact',
    hi: 'संपर्क करें',
    pa: 'ਸੰਪਰਕ',
    bn: 'যোগাযোগ',
    te: 'సంప్రదింపులు',
    ta: 'தொடர்பு',
    ml: 'ബന്ധപ്പെടുക',
    kn: 'ಸಂಪರ್ಕಿಸಿ',
    gu: 'સંપર્ક કરો',
    mr: 'संपर्क करा'
  },
  'footer.quick_links.title': {
    en: 'Quick Links',
    hi: 'त्वरित लिंक',
    pa: 'ਤੁਰੰਤ ਲਿੰਕ',
    bn: 'দ্রুত লিঙ্ক',
    te: 'త్వరిత లింక్‌లు',
    ta: 'விரைவு இணைப்புகள்',
    ml: 'ദ്രുത ലിങ്കുകൾ',
    kn: 'ತ್ವರಿತ ಲಿಂಕ್‌ಗಳು',
    gu: 'ઝડપી લિંક્સ',
    mr: 'त्वरित दुवे'
  },
  'footer.quick_links.how_it_works': {
    en: 'How It Works',
    hi: 'यह कैसे काम करता है',
    pa: 'ਇਹ ਕਿਵੇਂ ਕੰਮ ਕਰਦਾ ਹੈ',
    bn: 'এটি কীভাবে কাজ করে',
    te: 'ఇది ఎలా పని చేస్తుంది',
    ta: 'இது எவ்வாறு செயல்படுகிறது',
    ml: 'ഇത് എങ്ങനെ പ്രവർത്തിക്കുന്നു',
    kn: 'ಇದು ಹೇಗೆ ಕೆಲಸ ಮಾಡುತ್ತದೆ',
    gu: 'આ કેવી રીતે કામ કરે છે',
    mr: 'हे कसे काम करते'
  },
  'footer.quick_links.pricing': {
    en: 'Pricing',
    hi: 'मूल्य निर्धारण',
    pa: 'ਕੀਮਤ',
    bn: 'মূল্য',
    te: 'ధరలు',
    ta: 'விலை',
    ml: 'വിലനിർണ്ണയം',
    kn: 'ಬೆಲೆ',
    gu: 'કિંમત',
    mr: 'किंमत'
  },
  'footer.quick_links.features': {
    en: 'Features',
    hi: 'विशेषताएं',
    pa: 'ਵਿਸ਼ੇਸ਼ਤਾਵਾਂ',
    bn: 'বৈশিষ্ট্য',
    te: 'లక్షణాలు',
    ta: 'அம்சங்கள்',
    ml: 'സവിശേഷതകൾ',
    kn: 'ವೈಶಿಷ್ಟ್ಯಗಳು',
    gu: 'વિશેષતાઓ',
    mr: 'वैशिष्ट्ये'
  },
  'footer.legal.title': {
    en: 'Legal',
    hi: 'कानूनी',
    pa: 'ਕਾਨੂੰਨੀ',
    bn: 'আইনি',
    te: 'చట్టపరమైన',
    ta: 'சட்டப்பூர்வமான',
    ml: 'നിയമപരമായ',
    kn: 'ಕಾನೂನು',
    gu: 'કાનૂની',
    mr: 'कायदेशीर'
  },
  'footer.legal.privacy_policy': {
    en: 'Privacy Policy',
    hi: 'गोपनीयता नीति',
    pa: 'ਗੋਪਨੀਯਤਾ ਨੀਤੀ',
    bn: 'গোপনীয়তা নীতি',
    te: 'గోప్యతా విధానం',
    ta: 'தனியுரிமை கொள்கை',
    ml: 'സ്വകാര്യതാ നയം',
    kn: 'ಗೌಪ್ಯತೆ ನೀತಿ',
    gu: 'ગોપનીયતા નીતિ',
    mr: 'गोपनीयता धोरण'
  },
  'footer.legal.terms_of_service': {
    en: 'Terms of Service',
    hi: 'सेवा की शर्तें',
    pa: 'ਸੇਵਾ ਦੀਆਂ ਸ਼ਰਤਾਂ',
    bn: 'সেবার শর্তাবলী',
    te: 'సేవా నిబంధనలు',
    ta: 'சேவை விதிமுறைகள்',
    ml: 'സേവന നിബന്ധനകൾ',
    kn: 'ಸೇವೆಯ ನಿಬಂಧನೆಗಳು',
    gu: 'સેવાની શરતો',
    mr: 'सेवेच्या अटी'
  },
  'footer.legal.cookie_policy': {
    en: 'Cookie Policy',
    hi: 'कुकी नीति',
    pa: 'ਕੁਕੀ ਨੀਤੀ',
    bn: 'কুকি নীতি',
    te: 'కుకీ విధానం',
    ta: 'குக்கி கொள்கை',
    ml: 'കുക്കി നയം',
    kn: 'ಕುಕಿ ನೀತಿ',
    gu: 'કુકી નીતિ',
    mr: 'कुकी धोरण'
  },
  'footer.newsletter.title': {
    en: 'Newsletter',
    hi: 'न्यूज़लेटर',
    pa: 'ਨਿਊਜ਼ਲੈਟਰ',
    bn: 'নিউজলেটার',
    te: 'న్యూస్‌లెటర్',
    ta: 'செய்திமடல்',
    ml: 'ന്യൂസ്‌ലെറ്റർ',
    kn: 'ಸುದ್ದಿಪತ್ರಿಕೆ',
    gu: 'ન્યૂઝલેટર',
    mr: 'न्यूजलेटर'
  },
  'footer.newsletter.description': {
    en: 'Stay updated with our latest news and updates.',
    hi: 'हमारी नवीनतम समाचार और अपडेट के साथ अपडेट रहें।',
    pa: 'ਸਾਡੀਆਂ ਨਵੀਨਤਮ ਖਬਰਾਂ ਅਤੇ ਅਪਡੇਟਾਂ ਨਾਲ ਅਪਡੇਟ ਰਹੋ।',
    bn: 'আমাদের সর্বশেষ সংবাদ এবং আপডেটের সাথে আপ টু ডেট থাকুন।',
    te: 'మా తాజా వార్తలు మరియు నవీకరణలతో నవీకరించబడి ఉండండి।',
    ta: 'எங்கள் சமீபத்திய செய்திகள் மற்றும் புதுப்பிப்புகளுடன் புதுப்பிக்கப்பட்டு இருங்கள்।',
    ml: 'ഞങ്ങളുടെ ഏറ്റവും പുതിയ വാർത്തകളും അപ്ഡേറ്റുകളും ഉപയോഗിച്ച് അപ്ഡേറ്റ് ആയിരിക്കുക।',
    kn: 'ನಮ್ಮ ಇತ್ತೀಚಿನ ಸುದ್ದಿ ಮತ್ತು ನವೀಕರಣಗಳೊಂದಿಗೆ ನವೀಕರಿಸಲ್ಪಡಿರಿ।',
    gu: 'અમારી નવીનતમ સમાચાર અને અપડેટ્સ સાથે અપડેટ રહો।',
    mr: 'आमच्या नवीनतम बातम्या आणि अपडेटसह अपडेट रहा।'
  },
  'footer.newsletter.placeholder': {
    en: 'Your email address',
    hi: 'आपका ईमेल पता',
    pa: 'ਤੁਹਾਡਾ ਈਮੇਲ ਪਤਾ',
    bn: 'আপনার ইমেইল ঠিকানা',
    te: 'మీ ఇమెయిల్ చిరునామా',
    ta: 'உங்கள் மின்னஞ்சல் முகவரி',
    ml: 'നിങ്ങളുടെ ഇമെയിൽ വിലാസം',
    kn: 'ನಿಮ್ಮ ಇಮೇಲ್ ವಿಳಾಸ',
    gu: 'તમારું ઇમેઇલ સરનામું',
    mr: 'तुमचा ईमेल पत्ता'
  },
  'footer.newsletter.button': {
    en: 'Subscribe',
    hi: 'सदस्यता लें',
    pa: 'ਸਬਸਕ੍ਰਾਈਬ ਕਰੋ',
    bn: 'সাবস্ক্রাইব করুন',
    te: 'సభ్యత్వం పొందండి',
    ta: 'குழுசேர்',
    ml: 'സബ്‌സ്‌ക്രൈബ് ചെയ്യുക',
    kn: 'ಚಂದಾದಾರರಾಗಿ',
    gu: 'સબ્સ્ક્રાઇબ કરો',
    mr: 'सदस्यता घ्या'
  },
  'footer.newsletter.success': {
    en: 'Thank you for subscribing!',
    hi: 'सदस्यता लेने के लिए धन्यवाद!',
    pa: 'ਸਬਸਕ੍ਰਾਈਬ ਕਰਨ ਲਈ ਧੰਨਵਾਦ!',
    bn: 'সাবস্ক্রাইব করার জন্য ধন্যবাদ!',
    te: 'చందాదారీకి ధన్యవాదాలు!',
    ta: 'குழுசேர்ந்ததற்கு நன்றி!',
    ml: 'സബ്‌സ്‌ക്രൈബ് ചെയ്തതിന് നന്ദി!',
    kn: 'ಚಂದಾದಾರರಾಗಿದ್ದಕ್ಕಾಗಿ ಧನ್ಯವಾದಗಳು!',
    gu: 'સબ્સ્ક્રાઇબ કરવા બદલ આભાર!',
    mr: 'सदस्यता घेतल्याबद्दल धन्यवाद!'
  },
  'footer.copyright': {
    en: '© {year} Master Ji. All rights reserved.',
    hi: '© {year} मास्टर जी। सर्वाधिकार सुरक्षित।',
    pa: '© {year} ਮਾਸਟਰ ਜੀ। ਸਾਰੇ ਅਧਿਕਾਰ ਸੁਰੱਖਿਤ।',
    bn: '© {year} মাস্টার জি। সর্বস্বত্ব সংরক্ষিত।',
    te: '© {year} మాస్టర్ జీ। అన్ని హక్కులు రక్షించబడ్డాయి।',
    ta: '© {year} மாஸ்டர் ஜி। அனைத்து உரிமைகளும் பாதுகாக்கப்பட்டுள்ளன।',
    ml: '© {year} മാസ്റ്റർ ജി। എല്ലാ അവകാശങ്ങളും സംരക്ഷിച്ചിരിക്കുന്നു।',
    kn: '© {year} ಮಾಸ್ಟರ್ ಜಿ। ಎಲ್ಲಾ ಹಕ್ಕುಗಳನ್ನು ಕಾಯ್ದಿರಿಸಲಾಗಿದೆ।',
    gu: '© {year} માસ્ટર જી। બધા અધિકારો સુરક્ષિત છે।',
    mr: '© {year} मास्टर जी। सर्व हक्क राखीव।'
  },
  'footer.keep_in_touch': {
    en: 'Keep in Touch',
    hi: 'संपर्क में रहें',
    pa: 'ਸੰਪਰਕ ਵਿੱਚ ਰਹੋ',
    bn: 'যোগাযোগে থাকুন',
    te: 'సంప్రదింపులో ఉండండి',
    ta: 'தொடர்பில் இருங்கள்',
    ml: 'ബന്ധപ്പെട്ടിരിക്കുക',
    kn: 'ಸಂಪರ್ಕದಲ್ಲಿರಿ',
    gu: 'સંપર્કમાં રહો',
    mr: 'संपर्कात रहा'
  }
};

const languages: Language[] = [
  {
    code: 'en',
    name: 'English',
    nativeName: 'English',
    flag: '🇺🇸',
    direction: 'ltr'
  },
  {
    code: 'hi',
    name: 'Hindi',
    nativeName: 'हिंदी',
    flag: '🇮🇳',
    direction: 'ltr'
  },
  {
    code: 'pa',
    name: 'Punjabi',
    nativeName: 'ਪੰਜਾਬੀ',
    flag: '🇮🇳',
    direction: 'ltr'
  },
  {
    code: 'bn',
    name: 'Bengali',
    nativeName: 'বাংলা',
    flag: '🇧🇩',
    direction: 'ltr'
  },
  {
    code: 'te',
    name: 'Telugu',
    nativeName: 'తెలుగు',
    flag: '🇮🇳',
    direction: 'ltr'
  },
  {
    code: 'ta',
    name: 'Tamil',
    nativeName: 'தமிழ்',
    flag: '🇮🇳',
    direction: 'ltr'
  },
  {
    code: 'ml',
    name: 'Malayalam',
    nativeName: 'മലയാളം',
    flag: '🇮🇳',
    direction: 'ltr'
  },
  {
    code: 'kn',
    name: 'Kannada',
    nativeName: 'ಕನ್ನಡ',
    flag: '🇮🇳',
    direction: 'ltr'
  },
  {
    code: 'gu',
    name: 'Gujarati',
    nativeName: 'ગુજરાતી',
    flag: '🇮🇳',
    direction: 'ltr'
  },
  {
    code: 'mr',
    name: 'Marathi',
    nativeName: 'मराठी',
    flag: '🇮🇳',
    direction: 'ltr'
  }
];

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [isLoading, setIsLoading] = useState(false);

  // Load language from localStorage on mount
  useEffect(() => {
    const savedLanguage = localStorage.getItem('masterji-language');
    if (savedLanguage && languages.find(lang => lang.code === savedLanguage)) {
      setCurrentLanguage(savedLanguage);
    }
  }, []);

  // Save language to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('masterji-language', currentLanguage);
    
    // Update document direction
    const language = languages.find(lang => lang.code === currentLanguage);
    if (language) {
      document.documentElement.dir = language.direction;
      document.documentElement.lang = currentLanguage;
    }
  }, [currentLanguage]);

  const setLanguage = (languageCode: string) => {
    setIsLoading(true);
    setCurrentLanguage(languageCode);
    
    // Simulate loading time
    setTimeout(() => {
      setIsLoading(false);
    }, 500);
  };

  const t = (key: string): string => {
    const translation = translations[key];
    if (!translation) {
      console.warn(`Translation key not found: ${key}`);
      return key;
    }
    
    return translation[currentLanguage] || translation['en'] || key;
  };

  const value: LanguageContextType = {
    currentLanguage,
    setLanguage,
    t,
    languages,
    isLoading
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}; 